import type {OverlaySkinId} from './overlay-skin-id';
import {getChannelTheme} from './channel-theme';
import type {AtmosphereColors} from '../../remotion/msb/tokens';
import {TEXT} from '../../remotion/msb/text-colors';
import {wrapDisplayLineEm} from '../text/wrap-display-line';
/** 씬 키워드 표현 방식 — float(상단 대형 타이포)·badge(우상단 간판)·mega(중앙 초대형). */
type KeywordVariant = "float" | "badge" | "mega";

/** 16:9 오버레이 스킨 — 자막바 지오메트리·키워드 표현·인용 딤 색 프리셋. overlaySkin 미지정 = "knowdoc".
 *  ★knowdoc 기본값은 렌더 픽셀 불변 하드 계약(값 매핑은 각 필드 주석의 knowdoc= 참조). */
export type OverlaySkin = {
  /** 하단 자막바 배경 */
  captionBarBg: string;
  /** 하단 자막바 텍스트색(기본은 data.body.color 우선 · 배너색 오버라이드 시엔 이 값이 우선 — resolveCaptionTextColor) */
  captionText: string;
  /** 커스텀 서체 부재 시 자막 굵기(knowdoc=700) */
  captionWeight: number;
  /** 자막 최대 물리줄(초과분은 마지막 줄에 합침·knowdoc=2) */
  captionMaxRows: number;
  /**
   * 🔍 자막 접기 폭 = 화면폭 대비 비율. **미지정 = CAPTION_WIDTH_RATIO_DEFAULT(0.66) = 전 스킨 픽셀 불변.**
   * 브리핑/설계도 패밀리만 0.80 으로 넓힌다 — 규정·약관 문장은 길어서 0.66 폭 × 2줄 캡에 자주
   * 걸리고, 그때 foldRows 가 초과분을 **마지막 줄에 통째로 뭉쳐** 화면 밖으로 흘렀다(고객 제보
   * "글자가 작고 많다"의 다른 얼굴). 폭을 넓히고 줄 수를 늘려 **뭉침이 발생할 확률 자체**를 낮춘다.
   */
  captionWidthRatio?: number;
  /** 자막바 세로중심(height 비율·knowdoc=0.915) */
  captionCenterY: number;
  /** 자막바 모서리 반경(×S·knowdoc=12) */
  captionRadius: number;
  /** 자막바 가로 패딩(×S·knowdoc=44) */
  captionPadX: number;
  /** 자막바 세로 패딩(×S·knowdoc=18) */
  captionPadY: number;
  /** quote 따옴표 글리프 강조색 기본(overlay.color 가 우선·knowdoc=#3D5A99) */
  accent: string;
  /** 키워드 표현 방식(knowdoc="float") */
  keywordVariant: KeywordVariant;
  /** 키워드 메인 색 기본 — float 글자색·badge 배경·mega 글자색(overlay.color 가 우선·knowdoc=#3D5A99=accent) */
  keywordColor: string;
  /** quote 풀프레임 딤 배경 */
  quoteDim: string;
  /** quote 본문 잉크색 */
  quoteText: string;
  /** 자막 외곽선 색(시니어 스킨 — 투명 배경 위 초대형 자막의 가독 보장). 미지정=외곽선 없음(기존 불변). */
  captionStroke?: string;
  /** 자막 외곽선 두께(×S px). captionStroke 지정 시만 의미. */
  captionStrokeWidth?: number;
  /** 🎨 자막바 테두리 색(이슈 #1201 header.line 오버라이드 전용). 스킨 정의는 이 필드를 쓰지 않는다 —
   *  미지정 = 테두리 없음(전 스킨 픽셀 불변). */
  captionBarBorder?: string;
  /** 🎨 badge 키워드의 글자색(이슈 #1201 header.accent 오버라이드 전용·대비 가드 산물).
   *  미지정 = 종전 하드코딩 흰색(전 스킨 픽셀 불변). */
  keywordInk?: string;
};

// ── 🎨 헤더 색 오버라이드 계층(이슈 #1201) ────────────────────────────────────────────────
//  직접수정 DesignSection 의 배너색(header.bg)·윤곽선(header.line)·강조색(header.accent)은 code SSOT
//  에 정상 기록되지만, 16:9 렌더러는 헤더 바를 안 그리고 워터마크(title·emoji)만 써서 색이 화면에
//  한 번도 닿지 않았다(회귀 아님 — 처음부터 미배선). 이 계층이 스킨 상수 위에 그 색을 얹는다.
//
//  ★적용 게이트 = (header.themeLocked === true) AND (그 색이 채널 테마 주입값과 다름). 둘 다 필요하다.
//    ① "값이 있으면 적용" 은 성립하지 않는다 — channel-theme.applyChannelThemeToData 가 16:9 스타일
//      전건에서 header.bg/line/accent 를 채널 테마 색으로 이미 채워두기 때문에(blueprintLecture 포함),
//      값 유무로 판정하면 기존 작업물 전량이 스킨 상수 대신 테마색으로 재색된다(=대규모 회귀).
//    ② themeLocked 만으로도 부족하다(Codex P1) — applyStoryDesign 은 채널명·이모지만 바꿔도 같은
//      플래그를 세운다. 즉 "채널명만 고친 옛 16:9 작업물" 이 themeLocked 를 갖고 있고, 그 header 의
//      색은 여전히 테마 주입값이다. 필드별 저작 표식이 데이터에 없으므로, 값이 코드 기본 채널 테마와
//      **다를 때만** 저작으로 본다(= 테마가 넣은 값은 저작이 아니다). 테넌트 브랜드 오버라이드는
//      기본 테마와 다르므로 통과 — themeLocked 와 함께 서 있을 때만이라 의도한 브랜딩만 반영된다.
//    ③ 트레이드오프: 유저가 채널 테마와 **정확히 같은** hex 를 고르면 그 필드는 미적용(안전측 오탐).
//
//  ★스킨 공통 계층 — blueprintLecture 전용이 아니라 16:9 전 스킨에 동일 동작(스펙 §3).
//    스킨별 특수 토큰(captionStroke·captionMaxRows·지오메트리 등)은 건드리지 않는다.
//
//  ★★축별 스코프(회귀 수리 후 확정 — 김제하 제보 "배너색을 바꾸면 인포그래픽 씬 배경까지 바뀐다",
//    회장님 판정). UI 라벨이 곧 스코프다. 여기에 축을 더 얹지 말 것.
//      bg(배너색)   → **자막 바 배경 + 자막 글자 대비 보정만.** 씬 전체를 덮는 풀프레임 딤
//                     (skin.quoteDim = 인용 씬·브리핑 장표 딤)과 장표 팔레트는 스킨 상수 고정.
//                     ★이 축이 활성일 때만 대비 보정색이 authored data.body.color 를 이긴다
//                     (resolveCaptionTextColor — 템플릿 잉크의 "기본 배너" 전제가 깨지므로).
//      accent(강조색) → 키워드·따옴표 강조 + 장표 accent/accentDim/accentGlow.
//      line(윤곽선)  → 자막 바 테두리.

/** 오버라이드 입력 = StoryTemplate["header"] 중 이 계층이 읽는 필드만(구조적 부분집합). */
export type HeaderColorSource = {
  bg?: string;
  line?: string;
  accent?: string;
  themeLocked?: boolean;
} | null | undefined;

/** 채택된 오버라이드(모두 #RRGGBB 정규화). 어느 것도 없으면 null. */
export type HeaderColorOverride = { bg?: string; line?: string; accent?: string };

/** 이 스타일의 채널 테마가 header 에 주입하는 색(= 저작으로 치지 않는 값). channel-theme 매핑 미러:
 *  bg=barColor · line=borderColor · accent=accentColor.
 *  ★필드마다 **후보 여러 개**를 받는다 — 주입은 styleId 로 이뤄지지만(applyChannelThemeToData),
 *  구 작업물은 styleId 와 overlaySkin 이 갈린다(예: styleId="blueprint" + overlaySkin="briefing",
 *  둘 다 등록 테마인데 색이 다름). 어느 쪽 테마 색이든 일치하면 "주입값" 으로 보는 합집합이
 *  재색 회귀를 확실히 막는다(대가 = 오탐 창이 조금 넓어짐 — 안전측). */
export type ThemeInjectedColors = { bg?: string | string[]; line?: string | string[]; accent?: string | string[] } | null | undefined;

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** hex(#RGB·#RRGGBB)만 채택 → #RRGGBB 대문자. 그 외("transparent"·rgba()·빈 값·비문자열)는
 *  미채택(undefined) = 스킨 상수 유지(fail-safe). */
export function normHexColor(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const m = HEX_RE.exec(v.trim());
  if (!m) return undefined;
  const h = m[1];
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return `#${full.toUpperCase()}`;
}

/** header → 채택 오버라이드. themeLocked 아님/색 없음/전부 비hex/전부 테마 주입값 = null(=상수 그대로). */
export function headerColorOverride(header: HeaderColorSource, themeColors?: ThemeInjectedColors): HeaderColorOverride | null {
  if (!header || header.themeLocked !== true) return null;
  const authored = (v: unknown, themeV: string | string[] | undefined): string | undefined => {
    const hex = normHexColor(v);
    if (!hex) return undefined;
    const candidates = themeV === undefined ? [] : Array.isArray(themeV) ? themeV : [themeV];
    return candidates.some((t) => normHexColor(t) === hex) ? undefined : hex; // 테마가 넣은 값 = 저작 아님
  };
  const bg = authored(header.bg, themeColors?.bg);
  const line = authored(header.line, themeColors?.line);
  const accent = authored(header.accent, themeColors?.accent);
  if (!bg && !line && !accent) return null;
  return { ...(bg ? { bg } : {}), ...(line ? { line } : {}), ...(accent ? { accent } : {}) };
}

/** 스타일/스킨 id 들 → 채널 테마 주입색 **합집합**(코드 기본 레지스트리). 등록된 id 가 하나도
 *  없으면 undefined(비교 기준 없음 = themeLocked 색을 그대로 채택). */
export function themeInjectedColorsOf(...ids: Array<string | undefined>): ThemeInjectedColors {
  const bg: string[] = [];
  const line: string[] = [];
  const accent: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const theme = getChannelTheme(id);
    if (!theme) continue;
    bg.push(theme.barColor);
    line.push(theme.borderColor);
    accent.push(theme.accentColor);
  }
  return bg.length ? { bg, line, accent } : undefined;
}

const rgbOf = (hex: string): [number, number, number] => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** CSS 색의 알파만 추출 — rgba()/rgb()/#RRGGBBAA/#RRGGBB/"transparent". 해석 불가 = undefined. */
export function alphaOfCss(css: string | undefined): number | undefined {
  if (typeof css !== "string") return undefined;
  const s = css.trim().toLowerCase();
  if (s === "transparent") return 0;
  const rgba = /^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(s);
  if (rgba) {
    if (rgba[1] === undefined) return 1;
    const a = Number(rgba[1]);
    // "1.2.3"·"." 같은 malformed 알파는 NaN → 해석 불가로 반환(rgba(...,NaN) 생성 방지·Codex P2).
    return Number.isFinite(a) ? Math.max(0, Math.min(1, a)) : undefined;
  }
  const hex8 = /^#([0-9a-f]{8})$/.exec(s);
  if (hex8) return parseInt(hex8[1].slice(6), 16) / 255;
  if (HEX_RE.test(s)) return 1;
  return undefined;
}

/** hex + 알파 → rgba() 문자열. */
const withAlpha = (hex: string, alpha: number): string => {
  const [r, g, b] = rgbOf(hex);
  return `rgba(${r},${g},${b},${Math.round(Math.max(0, Math.min(1, alpha)) * 100) / 100})`;
};

/** WCAG 상대휘도(0~1). */
const relLuminance = (hex: string): number => {
  const f = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = rgbOf(hex);
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

/** WCAG 대비비(1~21). 두 인자 모두 #RRGGBB. */
export function contrastRatio(a: string, b: string): number {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** 자막 대비 하한 = WCAG AA 본문(4.5:1). 유저 색이 이 밑으로 떨어지면 텍스트색을 보정한다. */
export const MIN_CAPTION_CONTRAST = 4.5;

/** 🔍 자막 접기 폭 기본 비율(화면폭 대비) — 40+ 스킨 공통 상수. 스킨이 captionWidthRatio 를 주지
 *  않으면 이 값 = 종전 하드코딩 0.66 과 바이트 동일(전 스킨 픽셀 불변). */
export const CAPTION_WIDTH_RATIO_DEFAULT = 0.66;

/**
 * 🔍 브리핑·설계도 패밀리(컴플라이언스 계열) 자막 접기 규격 — 넓은 폭 + 3줄.
 *
 * ★적용 대상 = briefing·blueprintGov·blueprintSite·blueprintCorp·blueprintLecture·blueprintMotion·blueprintShowreel·blueprintColorCut 8종뿐★(모션 브리핑 09-30 · 쇼릴 브리핑 10-02 가족 합류 · 컬러컷 10-02 가족 합류).
 * 근거: 이 5종은 "BRIEFING_SKIN 복제 후 팔레트만 분화"한 한 가족이고, **보험 테넌트의 기본 스킨은
 * blueprintGov**(BLUEPRINT_INDUSTRY_DEFAULTS: insurance → "blueprint" → overlaySkin blueprintGov)라
 * 가족 전체를 함께 넓히지 않으면 정작 고객 영상이 빠진다. knowdoc 은 렌더 픽셀 불변 하드 계약이라
 * 제외(스펙 명시).
 *
 * ★폭 0.74 의 근거(P2-1 봉합)★ — 이 가족의 CSS 실효 폭은 `0.86(박스 maxWidth) − 2×48(padX)/1920`
 * = **0.81** 이다. 접기 목표를 0.80 으로 잡으면 여유가 1.25% 뿐이라, `visualEmWidth` 근사 오차만큼
 * 한 행이 실효 폭을 넘는 순간 브라우저가 **행을 한 번 더 접어** 계획에 없던 4번째 줄이 생기고
 * 자막바가 커져 프레임을 뚫는다. `safeCaptionFoldRatio`(실효 폭 × (1 − 8%)) = 0.745 아래인
 * 0.74 로 잡는다 — 종전 0.66 보다 12% 넓으면서 재줄바꿈 사고는 구조적으로 막는다.
 */
export const COMPLIANCE_CAPTION_FOLD = { captionMaxRows: 3, captionWidthRatio: 0.74 } as const;

/** bg 위에서 preferred 가 AA 미만이면 MSB TEXT 토큰(흰/검) 중 대비가 큰 쪽으로 교체.
 *  ★새 발명 아님 — "배경 명도로 textMain 을 흰/검 중 고르는" MSB resolveSceneColors 관용구를 그대로
 *  쓰되, 판정만 선언적 base 대신 실제 대비비로 한다(유저 색은 선언이 없으므로). */
export function readableTextOn(bg: string, preferred: string, min = MIN_CAPTION_CONTRAST): string {
  const surface = normHexColor(bg);
  if (!surface) return preferred; // 배경을 못 읽으면 판정 불가 → 종전 색 유지(fail-safe)
  const cur = normHexColor(preferred);
  if (cur && contrastRatio(cur, surface) >= min) return preferred;
  return contrastRatio(TEXT.mainLight, surface) >= contrastRatio(TEXT.mainDark, surface) ? TEXT.mainLight : TEXT.mainDark;
}

// 유저 배너색을 얹을 때 스킨의 알파를 해석 못 하거나(=0, 투명 자막바 스킨) 쓸 수 없을 때의 기본 불투명도.
//   투명 자막바 스킨에 알파 0 을 승계하면 "색을 골랐는데 여전히 안 보임" 이 되므로 실제 바를 그린다.
const CAPTION_BAR_ALPHA_FALLBACK = 0.62; // econstory 실측 자막바 알파

const usableAlpha = (css: string | undefined, fallback: number): number => {
  const a = alphaOfCss(css);
  return a === undefined || !Number.isFinite(a) || a < 0.02 ? fallback : a;
};

/**
 * 스킨 × 헤더 색 오버라이드 병합(순수).
 * - 오버라이드 없음 → **같은 참조 그대로 반환**(전 스킨 픽셀 불변 하드 계약).
 * - bg   → **자막 바 배경 한정** + 자막 텍스트 대비 보정
 * - accent → 인용 따옴표 강조색 + 키워드색(+ badge 글자색 대비 보정)
 * - line → 자막바 테두리(16:9 스킨엔 테두리 토큰이 없어 이 계층이 신설·미오버라이드 시 미렌더)
 *
 * ★#1201 회귀 수리(김제하 제보·회장님 판정) — 최초 구현은 bg 를 quoteDim 까지 밀어넣었는데,
 *   quoteDim 은 인용 씬(L~3400)과 장표 씬(브리핑 딤)에서 `inset:0` 풀프레임 필름으로 깔린다.
 *   그 결과 "배너색"을 고르면 인포그래픽/인용 씬 배경이 통째로 그 색으로 물들어(한 겹 덮어씌운
 *   느낌) 스킨 디자인이 날아갔다. **"배너색" 라벨의 스코프 = 자막 바**로 확정하고 딤은 스킨 상수로
 *   되돌린다. quoteText 대비 보정도 판정 기준(딤)이 사라졌으므로 함께 제거(오반전 방지).
 */
export function mergeSkinWithHeaderColors(skin: OverlaySkin, header: HeaderColorSource, themeColors?: ThemeInjectedColors): OverlaySkin {
  const ov = headerColorOverride(header, themeColors);
  if (!ov) return skin;
  const next: OverlaySkin = { ...skin };
  if (ov.bg) {
    next.captionBarBg = withAlpha(ov.bg, usableAlpha(skin.captionBarBg, CAPTION_BAR_ALPHA_FALLBACK));
    // 대비 가드 — 자막 잉크는 유저 배너색 위에서 읽혀야 한다(알파는 배경 이미지가 비쳐
    //   불확정이라 판정은 유저가 고른 색 자체를 기준으로 한다 = 디자이너 의도 기준).
    next.captionText = readableTextOn(ov.bg, skin.captionText);
    // ★quoteDim/quoteText 는 건드리지 않는다 — 위 주석의 #1201 회귀 수리 참조(풀프레임 딤 오염).
  }
  if (ov.accent) {
    next.accent = ov.accent;
    next.keywordColor = ov.accent;
    // badge 변형은 키워드색이 "배경", 글자는 종전 하드코딩 흰색 — 밝은 강조색을 고르면 흰 글자가
    //   사라진다(blueprintLecture 민트 #2FE0E0 = 흰 글자 1.6:1). 오버라이드가 있을 때만 보정.
    next.keywordInk = readableTextOn(ov.accent, TEXT.mainLight);
  }
  if (ov.line) next.captionBarBorder = ov.line;
  return next;
}

/**
 * 자막 바 텍스트 최종색 — 렌더의 `data.body?.color ?? skin.captionText` 우선순위를 결정하는 순수 함수.
 *
 * ★기본은 authored(data.body.color) 우선 = 종전 픽셀 불변. 단 **배너색 오버라이드가 활성이면
 *   병합층의 대비 보정색(skin.captionText)이 authored 를 이긴다.** 근거: 템플릿이 박아넣은 잉크색
 *   (makeBlueprintFamilyTemplate 의 fam.theme.ink 등)은 **스킨 기본 배너색을 전제로** 고른 값이라,
 *   유저가 배너색을 바꾸는 순간 그 전제가 깨진다 — 흰 잉크 + 밝은 배너 = 자막 실종. 전제가 무너진
 *   authored 값보다 가독성 보정이 우선한다(#1201 후속 — 보정이 authored 에 가려 무력하던 결함).
 * ★판정식은 headerColorOverride 재사용(병합층과 같은 게이트 — 판정 분기 금지).
 */
export function resolveCaptionTextColor(
  skin: OverlaySkin,
  authored: string | undefined,
  header: HeaderColorSource,
  themeColors?: ThemeInjectedColors,
): string {
  const ov = headerColorOverride(header, themeColors);
  if (ov?.bg) return skin.captionText; // 배너색 오버라이드 활성 = 보정색 승
  return authored ?? skin.captionText;
}

/**
 * 장표 팔레트(AtmosphereColors) × 헤더 색 오버라이드 병합(순수).
 * - accent → accent + accentDim/accentGlow(원래 알파 승계). **강조색 축만 전파한다.**
 * - bg → **미적용**(#1201 회귀 수리). 최초 구현은 배너색을 장표 팔레트 bg/panel/isDark/textMain/
 *   textSub 로 전파했지만 ①16:9 장표 레이아웃은 colors.bg 를 아예 칠하지 않아 실효가 없고
 *   ②panel·textMain 만 카드·글자를 배너색 쪽으로 끌고 가 "인포그래픽 씬이 배너색으로 물든다"는
 *   제보(김제하)의 2차 오염원이 됐다. 장표 팔레트는 스킨 고정.
 * - line → 장표 토큰(AtmosphereColors)에 테두리 필드가 없어 대응 없음(미적용).
 */
export function mergeSlideColorsWithHeaderColors(colors: AtmosphereColors, header: HeaderColorSource, themeColors?: ThemeInjectedColors): AtmosphereColors {
  const ov = headerColorOverride(header, themeColors);
  if (!ov || !ov.accent) return colors;
  const next: AtmosphereColors = { ...colors };
  next.accent = ov.accent;
  next.accentDim = withAlpha(ov.accent, alphaOfCss(colors.accentDim) ?? 0.14);
  next.accentGlow = withAlpha(ov.accent, alphaOfCss(colors.accentGlow) ?? 0.4);
  return next;
}

// knowdoc(세상의모든지식류) — 파스텔 동화책 + 흰 알약 자막 + 상단 float 잉크블루 키워드. ★이 스킨 = 픽셀 불변 계약.
export const KNOWDOC_SKIN: OverlaySkin = {
  captionBarBg: "rgba(255,255,255,0.96)", // 불투명 흰 알약(96%)
  captionText: "#3F3E3A", // 진회색
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.915,
  captionRadius: 12,
  captionPadX: 44,
  captionPadY: 18,
  accent: "#3D5A99", // 잉크 블루 — 벤치마크(세상의모든지식) 키워드 타이포 실측 계열·채널 테마 accent 와 동일.
  //   (최초 테라코타 #C05A2E 는 파스텔 웜톤 씬(밀짚모자 등)과 동화돼 가독 저하 — 프리뷰 육안 대조로 교체)
  keywordVariant: "float",
  keywordColor: "#3D5A99", // float 은 accent 와 동색 → knowdoc 키워드 출력 불변(ov.color ?? keywordColor === ov.color ?? accent)
  quoteDim: "rgba(250,247,240,0.86)", // 따뜻한 흰색 딤
  quoteText: "#3F3E3A",
};
// econstory(부자의경제학류) — 검정 반투명(0.62) 2줄 자막 + 우상단 빨간 간판 배지 키워드.
const ECONSTORY_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.62)",
  captionText: "#FFFFFF",
  captionWeight: 900,
  captionMaxRows: 2,
  captionCenterY: 0.88,
  captionRadius: 14,
  captionPadX: 52,
  captionPadY: 20,
  accent: "#D8402F", // 진빨강 — 경고판/네온 간판 계열
  keywordVariant: "badge",
  keywordColor: "#D8402F",
  quoteDim: "rgba(8,8,14,0.84)",
  quoteText: "#FFFFFF",
};
// infoknow(경제학 똑똑류) — 거의 불투명(0.86) 1줄 자막 + 중앙 초대형 옐로 mega 키워드.
const INFOKNOW_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.86)",
  captionText: "#FFFFFF",
  captionWeight: 900,
  captionMaxRows: 1,
  captionCenterY: 0.91,
  captionRadius: 10,
  captionPadX: 44,
  captionPadY: 16,
  accent: "#FFD84D", // 옐로 — mega 타이포/글로우 계열
  keywordVariant: "mega",
  keywordColor: "#FFD84D",
  quoteDim: "rgba(5,5,8,0.88)",
  quoteText: "#FFFFFF",
};
// 🧓 인생사연(시니어 롱폼 — 사연나무·뿌리깊은사연류 실측, 회장님 2026-07-16): 자막바 박스 없이
//   ★초대형 흰 900 + 검정 외곽선★ 자막이 화면 하단에 직접 얹힌다(시니어 가독 최우선 — 폰트가
//   기존의 1.6배는 팩토리 body.size=64 가 담당). 키워드는 mega(알록달록 옐로), 배경 딤은 다크.
const SENIORLIFE_SKIN: OverlaySkin = {
  captionBarBg: "transparent",
  captionText: "#FFFFFF",
  captionWeight: 900,
  captionMaxRows: 2,
  captionCenterY: 0.885,
  captionRadius: 0,
  captionPadX: 24,
  captionPadY: 8,
  keywordVariant: "mega",
  keywordColor: "#FFD84D",
  accent: "#FFB84D",
  quoteDim: "rgba(10,8,6,0.86)",
  quoteText: "#FFFFFF",
  captionStroke: "#000000",
  captionStrokeWidth: 5,
};

// ── 2차 배치 6종(회장님 2026-07-17 스카우트 실측 승인) ──
// 🌌 cosmos(실감나는우주류) — 자막바 없음(투명)+흰 글씨 얇은 검정 외곽선, mega 스케일 수치("광속의 10조 배").
const COSMOS_SKIN: OverlaySkin = {
  captionBarBg: "transparent",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 1,
  captionCenterY: 0.92,
  captionRadius: 0,
  captionPadX: 24,
  captionPadY: 8,
  accent: "#8FC1FF", // 스타 블루 — 성운/별빛 계열
  keywordVariant: "mega",
  keywordColor: "#FFFFFF", // 벤치 실측 = 스케일 수치는 순백 초대형
  quoteDim: "rgba(3,6,14,0.88)",
  quoteText: "#EAF2FF",
  captionStroke: "#000000",
  captionStrokeWidth: 3, // 별밭 위 얇은 외곽(seniorlife 5 대비 미니멀)
};
// 🌑 nightbook(깊은밤이야기류) — 자막바 없음+회백 소형 자막(문학 낭독), 키워드 극소(float·거의 미파생).
const NIGHTBOOK_SKIN: OverlaySkin = {
  captionBarBg: "transparent",
  captionText: "#D8D4CC",
  captionWeight: 500,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 0,
  captionPadX: 24,
  captionPadY: 8,
  accent: "#CBB68C", // 촛불 골드 — 흑백 화면 위 유일한 온기(따옴표 글리프)
  keywordVariant: "float",
  keywordColor: "#CBB68C",
  quoteDim: "rgba(10,10,10,0.9)",
  quoteText: "#E5E1D8",
  captionStroke: "#000000",
  captionStrokeWidth: 2, // 목탄 흰 영역 위 가독 보장(얇게)
};
// 🗃 darkcase(기묘한밤·미스스한이야기류) — 저투명 자막바+badge 섹션 라벨(우상단 간판)+노란 증언 인용.
const DARKCASE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.4)",
  captionText: "#E8E8E8",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 10,
  captionPadX: 44,
  captionPadY: 16,
  accent: "#E8C15A", // 증언 노랑(quote 따옴표 글리프)
  keywordVariant: "badge",
  keywordColor: "#B3261E", // 사건 레드 간판('사건의 시작' 코너 라벨 계열)
  quoteDim: "rgba(6,6,9,0.9)",
  quoteText: "#F0EDE6",
};
// ⭐ famefall(달빛부부류) — 반투명 다크 자막바 대형 문장 + 금액·숫자 골드 mega('2000억 탕진').
const FAMEFALL_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.88,
  captionRadius: 14,
  captionPadX: 52,
  captionPadY: 20,
  accent: "#FFC24D", // 스포트라이트 골드
  keywordVariant: "mega",
  keywordColor: "#FFC24D",
  quoteDim: "rgba(12,9,12,0.88)",
  quoteText: "#FFFFFF",
};
// 🌙 sleepdocu(잘듣세류) — 자막바 없음+양피지 크림 소형 자막(수면 낭독·비침습), 키워드 절제.
const SLEEPDOCU_SKIN: OverlaySkin = {
  captionBarBg: "transparent",
  captionText: "#E8DCC4",
  captionWeight: 500,
  captionMaxRows: 1,
  captionCenterY: 0.93,
  captionRadius: 0,
  captionPadX: 24,
  captionPadY: 8,
  accent: "#C9A96A", // 세피아 골드
  keywordVariant: "float",
  keywordColor: "#C9A96A",
  quoteDim: "rgba(22,16,8,0.88)",
  quoteText: "#EFE6D2",
  captionStroke: "#1A1208",
  captionStrokeWidth: 2,
};
// 🕯 horrorrank(숫노루류) — 진한 다크 자막바+빨강 mega 랭킹 타이틀(infoknow mega 구조·색만 호러 레드).
const HORRORRANK_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.7)",
  captionText: "#FFFFFF",
  captionWeight: 900,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 10,
  captionPadX: 44,
  captionPadY: 16,
  accent: "#FF3B30", // 블러드 레드
  keywordVariant: "mega",
  keywordColor: "#FF3B30",
  quoteDim: "rgba(10,3,3,0.9)",
  quoteText: "#FFE9E6",
};

// ── 3차 배치 2종(회장님 2026-07-18 딥 스카우트 승인) ──
// 📉 stickfall(당신이몰랐던이야기 문법 실측+김히엉 방법론) — 다크바 흰 자막+레드 mega '몰락' 키워드+노랑 인용구.
const STICKFALL_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.6)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 18,
  accent: "#FFD84D", // 노랑 인용구(벤치의 계단식 후킹 텍스트 계열)
  keywordVariant: "mega",
  keywordColor: "#E5484D", // '몰락' 레드 — 절정 국면 초대형 키워드
  quoteDim: "rgba(8,8,10,0.88)",
  quoteText: "#FFFFFF",
};
// 🌸 jinsei(フミコの物語·人生100年物語류 실측) — 반투명 다크바+흰 자막(NotoSansJP), 키워드 절제·인용 중심.
const JINSEI_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.45)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#E8A7B8", // 사쿠라 — 따옴표 글리프(감정 인용)
  keywordVariant: "float",
  keywordColor: "#E8A7B8",
  quoteDim: "rgba(20,12,14,0.88)",
  quoteText: "#F5EDEF",
};

// ── 4차 배치 11종(회장님 2026-07-18 "풀완비" 딥 스카우트) — JP 5종+KR 6종 ──
// 💥 gyakuten(スカッと 역전극) — 반투명 다크바+흰 자막(NotoSansJP), badge 가해자 라벨+오렌지 인용.
const GYAKUTEN_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFB13D", // 앰버 — 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#E8503A", // 가해자 라벨 간판 레드
  quoteDim: "rgba(15,10,8,0.88)",
  quoteText: "#FFF6EE",
};
// 🏯 sengoku(AI Historia 워드라마) — 반투명 다크바+흰 900 자막(NotoSansJP), red mega 사건 키워드+금 인용.
const SENGOKU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#D4AF37", // 금박 — 무장 독백 따옴표
  keywordVariant: "mega",
  keywordColor: "#E63946", // 결전 레드
  quoteDim: "rgba(10,6,4,0.9)",
  quoteText: "#F5EBDC",
};
// 👹 kaidan(午前二時·奇々怪々 괴담 낭독) — 자막바 없음+회백 소형 자막(NotoSansJP), float 청 키워드·정적 문법.
const KAIDAN_SKIN: OverlaySkin = {
  captionBarBg: "transparent",
  captionText: "#E8E4DC",
  captionWeight: 600,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 0,
  captionPadX: 24,
  captionPadY: 8,
  accent: "#9BB8D4", // 창백한 청 — 어둠 속 유일한 냉기(따옴표 글리프)
  keywordVariant: "float",
  keywordColor: "#9BB8D4",
  quoteDim: "rgba(6,8,12,0.92)",
  quoteText: "#DCE4EC",
  captionStroke: "#000000",
  captionStrokeWidth: 3, // 야간 실사 어둠 위 가독 보장
};
// 🏰 teikoku(世界見聞録 제국 흥망) — 반투명 다크바+흰 900 자막(NotoSansJP), red mega 연도/수치+보라 인용.
const TEIKOKU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.6)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#D4AF37", // 제국 금색 — 왕조 어록 따옴표
  keywordVariant: "mega",
  keywordColor: "#D62828", // 붕괴 레드
  quoteDim: "rgba(8,6,10,0.9)",
  quoteText: "#F0EAF2",
};
// 🌕 nemureru(ぐっすり眠れる歴史 수면 역사) — 자막바 없음+청록 소형 1줄 자막(NotoSansJP), 키워드 극소·비침습.
const NEMURERU_SKIN: OverlaySkin = {
  captionBarBg: "transparent",
  captionText: "#D8E2E8",
  captionWeight: 500,
  captionMaxRows: 1,
  captionCenterY: 0.93,
  captionRadius: 0,
  captionPadX: 24,
  captionPadY: 8,
  accent: "#D4C08A", // 달빛 골드 — 따옴표 글리프
  keywordVariant: "float",
  keywordColor: "#D4C08A",
  quoteDim: "rgba(8,14,18,0.88)",
  quoteText: "#E4ECE8",
  captionStroke: "#0A1418",
  captionStrokeWidth: 2, // 남색 야경 위 가독(얇게)
};
// 🏮 yadam(구름야담 민담 구연) — 반투명 다크바+흰 자막, 주황 mega 정체/재주 키워드+담채 인용.
const YADAM_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F2A65A", // 등불 주황 — 구연 대사 따옴표
  keywordVariant: "mega",
  keywordColor: "#F2A65A",
  quoteDim: "rgba(16,10,6,0.88)",
  quoteText: "#F8EFDF",
};
// ⚖️ verdict(처벌실화 판결 서사) — 반투명 다크바+흰 900 자막, 크림슨 mega 형량 키워드+금 인용.
const VERDICT_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.6)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFD84D", // 저울 금색 — 발언 따옴표
  keywordVariant: "mega",
  keywordColor: "#C9184A", // 판결 크림슨
  quoteDim: "rgba(8,8,12,0.9)",
  quoteText: "#F2F2F6",
};
// 🗺 geomap(지정학연구소·지식브런치 지정학 해설) — 반투명 다크바+흰 자막, badge 지역/수치 라벨+무채 인용.
const GEOMAP_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.6)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#E76F51", // 지도 테라코타 — 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#E9C46A", // 사금색 지역/수치 라벨(중립 다큐 톤)
  quoteDim: "rgba(6,8,10,0.9)",
  quoteText: "#F0F2EC",
};
// 🪷 wisdom(지혜의바다 명언 낭독) — 자막바 없음+크림 소형 1줄 자막, ★인용 딤 패널이 주역(quote 중심).
const WISDOM_SKIN: OverlaySkin = {
  captionBarBg: "transparent",
  captionText: "#EFE8D8",
  captionWeight: 500,
  captionMaxRows: 1,
  captionCenterY: 0.93,
  captionRadius: 0,
  captionPadX: 24,
  captionPadY: 8,
  accent: "#A8C3A0", // 세이지 그린 — 잠언 따옴표 글리프(주역)
  keywordVariant: "float",
  keywordColor: "#A8C3A0",
  quoteDim: "rgba(10,16,12,0.88)",
  quoteText: "#F0EFE4",
  captionStroke: "#0A100C",
  captionStrokeWidth: 2, // 심록 풍경 위 가독(얇게)
};
// ❓ gunggeum(사물궁이 생활 궁금증 도해) — ★유일한 밝은 스킨: 흰 알약 자막바(0.94)+어두운 잉크+badge 개념 라벨.
const GUNGGEUM_SKIN: OverlaySkin = {
  captionBarBg: "rgba(255,255,255,0.94)", // 밝은 알약(knowdoc 계열)
  captionText: "#2A2A2E", // ★어두운 잉크(밝은 자막바 대비)
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.915,
  captionRadius: 12,
  captionPadX: 44,
  captionPadY: 16,
  accent: "#4A90D9", // 도해 블루 — 통념 인용 따옴표
  keywordVariant: "badge",
  keywordColor: "#4A90D9", // 개념 라벨 블루
  quoteDim: "rgba(240,244,248,0.92)", // 밝은 딤(라이트 스킨)
  quoteText: "#2A2A2E",
};
// 🧪 trivia(몽구로운지식 과학 트리비아) — 반투명 다크바+흰 900 자막, 오렌지 mega 수치 키워드+웜 인용.
const TRIVIA_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FF8C42", // 실험 오렌지 — 사실 인용 따옴표
  keywordVariant: "mega",
  keywordColor: "#FF8C42",
  quoteDim: "rgba(10,8,6,0.9)",
  quoteText: "#FFF2E8",
};
// 📊 briefing(경제 브리핑·한경 모닝루틴류 실측) — 실사 보도 스틸+풀프레임 장표(PPT) 교차. 다크 네이비
//   자막바, 키워드는 장표가 대신하므로 절제(badge — 파생은 keywords:false 라 명시 시에만).
const BRIEFING_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,14,22,0.7)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  ...COMPLIANCE_CAPTION_FOLD, // 🔍 규정 문장 뭉침 완화(3줄·폭 80%) — 근거는 상수 주석
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#3E8EF7", // 브리핑 블루 — 전문가 코멘트 인용 글리프
  keywordVariant: "badge",
  keywordColor: "#3E8EF7",
  quoteDim: "rgba(6,10,18,0.9)", // ★장표 패널 딤도 이 값을 재사용(신규 스킨 필드 없음)
  quoteText: "#F2F6FC",
};
// 🧭 principleExplainer(#169) — 산업별 "원리" 설명형이 공유하는 AI 시네마틱 스킨.
// 풀프레임 AI 스틸을 살리고 하단 자막은 두 줄로 억제하며, 키 숫자·단어만 청록 badge로 짚는다.
const PRINCIPLE_EXPLAINER_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6, 13, 22, 0.66)",
  captionText: "#F8FAFC",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F6C453",
  keywordVariant: "badge",
  keywordColor: "#0F766E",
  quoteDim: "rgba(5, 10, 18, 0.82)",
  quoteText: "#F8FAFC",
};
/** 📊 브리핑 장표 팔레트(MSB AtmosphereColors 계약) — BRIEFING_SKIN·BRIEFING_LAYOUT 과 동일 계열. */
export const BRIEFING_SLIDE_COLORS: AtmosphereColors = {
  bg: "#0B1220",
  panel: "#131C2E",
  accent: "#3E8EF7",
  accentDim: "rgba(62,142,247,0.18)",
  accentGlow: "rgba(62,142,247,0.45)",
  isDark: true,
  // ★textMain/textSub 누락 = 장표 제목·라벨이 color:undefined 로 상속돼 침침하던 선재 결함
  //   (회장님 07-17 "프리뷰가 어둡다" 실측 — layouts 는 C.textMain/textSub 만 쓴다·tokens:887 계약).
  textMain: "#FFFFFF",
  textSub: "#CCD0DE",
};

// ── 📐 설계도 패밀리 스킨 4종(PR-3·회장님 결정: blueprint=Gov 흡수) — BRIEFING_SKIN 복제 후
//    가족 팔레트만 분화. 장표 딤(quoteDim)이 각 가족 배경과 균형이 되게 개별 튜닝(니어블랙 수리 R1).
// 📐 기관 브리핑(Gov·영천시류 라이트 파스텔) — 밝은 배경 위 다크 잉크 자막.
const BLUEPRINT_GOV_SKIN: OverlaySkin = {
  captionBarBg: "rgba(255,255,255,0.88)",
  captionText: "#1F2A44",
  captionWeight: 700,
  ...COMPLIANCE_CAPTION_FOLD, // 🔍 규정 문장 뭉침 완화(3줄·폭 80%) — 근거는 상수 주석
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#3B6FE0",
  keywordVariant: "badge",
  keywordColor: "#3B6FE0",
  quoteDim: "rgba(240,244,250,0.78)", // 라이트 장표 캔버스 — 배경이 은은히 비침
  quoteText: "#1F2A44",
};
// 🏗🌅 아침 안전 조회 스튜디오(10-10 · 회장님 확정 v3 «오케이 이 느낌으로») — 밝은 회색 스튜디오 벽 위 흰 카드 · 자막은 검정 반투명 띠+흰 글자.
//   ★«아침 안전 조회» 틀 조립기만 이 피부를 건다★(스타일 갤러리·업종 기본값 밖) — 다른 작업물의 픽셀은 바뀌지 않는다.
const BLUEPRINT_STUDIO_SKIN: OverlaySkin = {
  captionBarBg: "rgba(20,26,36,0.92)",
  captionText: "#FFFFFF",
  captionWeight: 600,
  captionMaxRows: 2, // 폭 비율은 기본(진행자 구도면 PRESENTER_HOST 가 덮는다)
  captionCenterY: 0.9,
  captionRadius: 14,
  captionPadX: 26,
  captionPadY: 10,
  accent: "#249158",
  keywordVariant: "badge",
  keywordColor: "#249158",
  quoteDim: "rgba(0,0,0,0)", // 스튜디오 벽이 그대로 보인다(장표는 흰 카드가 스스로 선다)
  quoteText: "#18202C",
};
// 🏗 현장 브리핑(Site·H리더스류) — 실사 배경이 비치는 저투명 딤+세이프티 오렌지.
const BLUEPRINT_SITE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,14,22,0.72)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  ...COMPLIANCE_CAPTION_FOLD, // 🔍 규정 문장 뭉침 완화(3줄·폭 80%) — 근거는 상수 주석
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FF9A3D",
  keywordVariant: "badge",
  keywordColor: "#FF9A3D",
  quoteDim: "rgba(10,14,22,0.52)", // ★저투명 — 현장 실사 배경이 살아있게(스펙 R1)
  quoteText: "#FFF4E8",
};
// 🏢 기업 하이라이트(Corp·OI 데모데이류) — 프리미엄 다크+시안.
const BLUEPRINT_CORP_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,10,20,0.72)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  ...COMPLIANCE_CAPTION_FOLD, // 🔍 규정 문장 뭉침 완화(3줄·폭 80%) — 근거는 상수 주석
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EE7F9",
  keywordVariant: "badge",
  keywordColor: "#6EE7F9",
  quoteDim: "rgba(6,8,16,0.58)",
  quoteText: "#EAFBFF",
};
// 🎓 아바타 강의(Lecture·dmex/fnk 딥블루류) — 학습 무드 딥블루+민트.
const BLUEPRINT_LECTURE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,14,44,0.74)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  ...COMPLIANCE_CAPTION_FOLD, // 🔍 규정 문장 뭉침 완화(3줄·폭 80%) — 근거는 상수 주석
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#2FE0E0",
  keywordVariant: "badge",
  keywordColor: "#2FE0E0",
  quoteDim: "rgba(8,14,44,0.6)",
  quoteText: "#E8FBFF",
};
// 🧊 모션 브리핑(2026-09-30) — 밝은 회색 캔버스 + 브랜드색 1개. 자막 띠는 **흰 띠 위 먹색 글자**.
//   ★가족 본문색(theme.ink = 먹)이 작업물 `body.color` 로 직렬화돼 자막 글자색이 된다★ — 짙은 띠를 쓰면 새로 만든 작업물 전부에서
//   먹 글자가 먹 띠에 묻혔다(09-30 견본 실측). 밝은 띠는 먹·브랜드 도형 위에서도 띠가 보인다.
//   ★quoteDim 은 불투명 캔버스색★ — 장표 씬은 씬 그림 대신 캔버스를 본다(원쉐이프 화법 · 그림은 비용만 든다).
const BLUEPRINT_MOTION_SKIN: OverlaySkin = {
  captionBarBg: "rgba(255,255,255,0.94)",
  captionText: "#111827",
  captionWeight: 700,
  ...COMPLIANCE_CAPTION_FOLD, // 🔍 규정 문장 뭉침 완화(3줄·폭 80%) — 가족 공통
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#2F6BFF",
  keywordVariant: "badge",
  keywordColor: "#2F6BFF",
  quoteDim: "#EEF0F3",
  quoteText: "#111827",
};

// 🎬 쇼릴 브리핑(가칭 · 2026-10-02) — 어두운 무대(먹 바탕) 위 짙은 띠 + 흰 글자. 강조 원색 1개(테넌트 색이 있으면 그것).
//   ★quoteDim 은 불투명 바탕색★ — 장표 씬은 씬 그림 대신 무대를 본다(모션 무대 가족).
const BLUEPRINT_SHOWREEL_SKIN: OverlaySkin = {
  captionBarBg: "rgba(17,24,39,0.82)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  ...COMPLIANCE_CAPTION_FOLD, // 🔍 규정 문장 뭉침 완화(3줄·폭 80%) — 가족 공통
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FF5A1F",
  keywordVariant: "badge",
  keywordColor: "#FF5A1F",
  quoteDim: "#0B0D12",
  quoteText: "#FFFFFF",
};

// 🎨 컬러컷(2026-10-02) — 자막 띠 = 먹 띠 + 흰 글자(고정). 색면이 장면마다 바뀌어도(파랑·빨강·청록·초록 … 밝은 브랜드색이면 노랑까지)
//   띠가 묻히지 않는다 — 팔레트가 «먹 띠와 장면색 대비 ≥ 1.8» 을 잠근다(`color-cut-palette.ts`). 흰 띠는 밝은 색면에서 경계가 흐려졌다.
const BLUEPRINT_COLORCUT_SKIN: OverlaySkin = {
  captionBarBg: "rgba(17,24,39,0.82)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  ...COMPLIANCE_CAPTION_FOLD, // 🔍 규정 문장 뭉침 완화(3줄·폭 80%) — 가족 공통
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#2F5BFF",
  keywordVariant: "badge",
  keywordColor: "#2F5BFF",
  quoteDim: "#2956FF",
  quoteText: "#FFFFFF",
};

/** 📐 스킨별 장표 콘텐츠 팔레트(AtmosphereColors) — 가족 스킨과 동일 계열. 렌더 지점이 overlaySkin
 *  키로 조회(0-배관 — PR-3 스펙 §2.3). 미등재 스킨(브리핑극장 포함 비패밀리)=BRIEFING_SLIDE_COLORS. */
export const SLIDE_COLORS_BY_SKIN: Partial<Record<string, AtmosphereColors>> = {
  briefing: BRIEFING_SLIDE_COLORS,
  blueprintGov: {
    bg: "#F4F7FB", panel: "#FFFFFF", accent: "#3B6FE0",
    accentDim: "rgba(59,111,224,0.14)", accentGlow: "rgba(59,111,224,0.35)",
    isDark: false, textMain: "#14213D", textSub: "#5A6478",
  },
  blueprintStudio: {
    bg: "#ECEDEF", panel: "#FFFFFF", accent: "#249158",
    accentDim: "rgba(36,145,88,0.14)", accentGlow: "rgba(36,145,88,0.32)",
    isDark: false, textMain: "#18202C", textSub: "#606A78",
  },
  blueprintSite: {
    bg: "#0C1017", panel: "#161C26", accent: "#FF9A3D",
    accentDim: "rgba(255,154,61,0.16)", accentGlow: "rgba(255,154,61,0.4)",
    isDark: true, textMain: "#FFFFFF", textSub: "#D8DDE6",
  },
  blueprintCorp: {
    bg: "#0C1020", panel: "#141A30", accent: "#6EE7F9",
    accentDim: "rgba(110,231,249,0.14)", accentGlow: "rgba(110,231,249,0.4)",
    isDark: true, textMain: "#FFFFFF", textSub: "#C9D4E8",
  },
  blueprintLecture: {
    bg: "#0C1D5F", panel: "#14276F", accent: "#2FE0E0",
    accentDim: "rgba(47,224,224,0.14)", accentGlow: "rgba(47,224,224,0.4)",
    isDark: true, textMain: "#FFFFFF", textSub: "#CDD9FF",
  },
  blueprintMotion: {
    bg: "#EEF0F3", panel: "#F4F6F9", accent: "#2F6BFF",
    accentDim: "rgba(47,107,255,0.12)", accentGlow: "rgba(47,107,255,0.32)",
    isDark: false, textMain: "#111827", textSub: "#4B5563",
  },
  blueprintShowreel: {
    bg: "#0B0D12", panel: "#151922", accent: "#FF5A1F",
    accentDim: "rgba(255,90,31,0.16)", accentGlow: "rgba(255,90,31,0.4)",
    isDark: true, textMain: "#FFFFFF", textSub: "#D5D9E0",
  },
  // 🎨 컬러컷 — `accent` = 기본 브랜드색(장면색 4개의 씨앗). 장표 글자색은 무대가 장면색마다 다시 정한다(`colorCutContentColors`).
  blueprintColorCut: {
    bg: "#2956FF", panel: "#1F45D6", accent: "#2F5BFF",
    accentDim: "rgba(0,0,0,0.22)", accentGlow: "rgba(255,255,255,0.55)",
    isDark: true, textMain: "#FFFFFF", textSub: "#FFFFFF",
  },
};

// ── 🏗 배치5 건설·산업안전 16:9 3종(회장님 2026-07-18) — 스킨 색은 batch5 스펙 정본 ──
// 🏗 gongdocu(현장기록 극사실 다큐) — 저투명 다크 자막바+세이프티오렌지 badge 국면 라벨(위험요인·사고원인·안전수칙)+옐로 증언/규정 인용.
const GONGDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFD84D", // 옐로 — 증언·규정 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FF6A00", // 세이프티 오렌지 badge(국면 라벨)
  quoteDim: "rgba(8,6,3,0.9)",
  quoteText: "#FFF4E6",
};
// ⛑ safetoon(안전툰 2D 시트콤 카툰) — 저투명 자막바+카툰옐로 mega 사고 키워드+스카이블루 대사 인용.
const SAFETOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(20,16,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이블루 — 대사 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFD400", // 카툰 옐로 mega(사고 키워드)
  quoteDim: "rgba(10,8,3,0.9)",
  quoteText: "#EAF6FF",
};
// 🦺 zoosite(동물현장 3D 동물 크루) — 저투명 자막바+라임그린 badge 미션/수칙 라벨+웜오렌지 캐릭터 대사.
const ZOOSITE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,18,12,0.45)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFB347", // 웜오렌지 — 캐릭터 대사 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#7ED957", // 라임그린 badge(미션·수칙 라벨)
  quoteDim: "rgba(4,10,6,0.9)",
  quoteText: "#F0FFF4",
};

// ── 🏗 배치5b 건설·산업안전 확장 16:9 5종(회장님 2026-07-18 "그리드 꽉 채우자") — 스킨 색은 batch5b 스펙 정본 ──
// ⚖️ courtcase(판례극장 법정 다큐) — 저투명 다크 자막바+딥레드 badge 쟁점 라벨(쟁점·판결·책임)+골드 판결/증언 인용.
const COURTCASE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.6)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#E6C36B", // 골드 — 판결문·증언 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#C0392B", // 딥레드 badge(쟁점 라벨)
  quoteDim: "rgba(10,6,4,0.9)",
  quoteText: "#F5EAD6",
};
// 📐 structlab(구조연구소 안전공학 도해) — 저투명 블루 자막바+시안 badge 원리 라벨(하중·임계점·안전율)+화이트 공학 격언 인용.
const STRUCTLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,14,26,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 공학 격언·규정 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#29B6F6", // 시안 badge(원리 라벨)
  quoteDim: "rgba(4,10,20,0.9)",
  quoteText: "#EAF6FF",
};
// 🎖 myungjang(명장록 베테랑 구술 다큐) — 투명 자막바(웜화이트 대형 자막)+브론즈 구술 인용만(키워드 파생 없음·quote 만).
const MYUNGJANG_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.25)",
  captionText: "#F3EDE4",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#CD9B62", // 브론즈 — 구술 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#CD9B62",
  quoteDim: "rgba(12,9,6,0.88)",
  quoteText: "#F3E8D8",
};
// 🕰 sagorecord(현장연대기 역사 아카이브) — 저투명 다크 자막바+앰버 mega 연도·수치+스틸그레이 조사보고서 인용.
const SAGORECORD_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#B0BEC5", // 스틸그레이 — 조사보고서 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFB300", // 앰버 mega(연도·수치)
  quoteDim: "rgba(8,8,8,0.9)",
  quoteText: "#ECEFF1",
};
// 💰 safenomics(안전경제 손익 다큐) — 저투명 그린블랙 자막바+머니그린 mega 금액·배수+화이트 경영 격언 인용.
const SAFENOMICS_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,12,10,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 경영 격언 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#00C853", // 머니그린 mega(금액·배수)
  quoteDim: "rgba(4,10,8,0.9)",
  quoteText: "#E8F5E9",
};
// 📋 sagobrief(사고 사례 브리핑) — 진한 자막바+레드 badge(사례·개요)·노랑 badge(예방 — 세그 color)+크림 TBM 인용 카드.
//   keywordInk = 배지 바탕색에 맞춰 글자색을 고르게 한다(노랑 바탕에 흰 글씨 방지).
const SAGOBRIEF_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.6)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFC400", // 노랑 — TBM 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#E53935", // 레드 badge(중대재해 사례·사고 개요)
  keywordInk: "#111111",
  quoteDim: "rgba(10,10,12,0.9)",
  quoteText: "#FFF8E1",
};

// ── 🛡 보험 테넌트 특화 16:9 8종(회장님 2026-07-18 세계관 확장 Step 1) — 스킨 색은 보험 스펙 정본 ──
// ⚖️ insucase(보상극장 분쟁 예방 다큐) — 저투명 다크 자막바+딥레드 badge 쟁점 라벨(쟁점·판단·예방)+골드 조정례/용어 인용.
const INSUCASE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.6)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#E6C36B", // 골드 — 조정례·용어 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#C0392B", // 딥레드 badge(쟁점 라벨)
  quoteDim: "rgba(10,6,4,0.9)",
  quoteText: "#F5EAD6",
};
// 📑 yakgwanlab(약관연구소 용어·원리 도해) — 저투명 블루 자막바+시안 badge 원리 라벨(용어·원리·체크)+화이트 원리 인용. 무인물 도해.
const YAKGWANLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,12,26,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 원리 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#29B6F6", // 시안 badge(원리 라벨)
  quoteDim: "rgba(4,10,20,0.9)",
  quoteText: "#EAF6FF",
};
// 👨‍👩‍👧 lifeplan(인생보장극장 생애주기 감성 서사) — 저투명 웜 자막바+웜오렌지 badge 생애 이벤트 라벨+크림 가족 인용.
const LIFEPLAN_SKIN: OverlaySkin = {
  captionBarBg: "rgba(20,12,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFE8C9", // 크림 — 가족의 말 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FF8C42", // 웜오렌지 badge(생애 이벤트 라벨)
  quoteDim: "rgba(14,8,4,0.88)",
  quoteText: "#FFF3E0",
};
// 🕵️ fraudfile(사기조사파일 SIU 재연 다큐) — 저투명 다크 자막바+레드 mega 수법·통계+스틸그레이 조사기록 인용.
const FRAUDFILE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.6)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#B0BEC5", // 스틸그레이 — 조사 기록 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FF3B30", // 레드 mega(수법·통계 수치)
  quoteDim: "rgba(8,8,8,0.9)",
  quoteText: "#ECEFF1",
};
// 🃏 insushow(보험툰극장 웹툰풍 2D 코믹 카툰) — safetoon 구조 복제. 저투명 딥브라운 자막바+카툰옐로 mega 소동 키워드+스카이 대사 인용.
const INSUSHOW_SKIN: OverlaySkin = {
  captionBarBg: "rgba(20,16,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이블루 — 대사 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFD400", // 카툰 옐로 mega(소동 키워드)
  quoteDim: "rgba(10,8,3,0.9)",
  quoteText: "#EAF6FF",
};
// 🎖 fpchronicle(설계사록 구술 신뢰 다큐) — 투명 자막바(웜화이트 대형 자막)+브론즈 구술 인용만(키워드 파생 없음·quote 만).
const FPCHRONICLE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.25)",
  captionText: "#F3EDE4",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#CD9B62", // 브론즈 — 구술 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#CD9B62",
  quoteDim: "rgba(12,9,6,0.88)",
  quoteText: "#F3E8D8",
};
// 🖍 moneyclass(칠판클래스 화이트보드 손그림 강의) — 다크 자막바 유지(밝은 이미지 위 가독)+마커레드 badge 개념 라벨(개념·실천·체크)+마커블루 핵심 인용.
const MONEYCLASS_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,14,20,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#1E88E5", // 마커블루 — 핵심 문장 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#E53935", // 마커레드 badge(개념 라벨)
  quoteDim: "rgba(6,10,16,0.9)",
  quoteText: "#EAF2FF",
};
// 🤖 insumate(보험메이트 마스코트 설명형) — 저투명 자막바+라벤더퍼플 mega 핵심 용어+스카이 캐릭터 대사 인용.
const INSUMATE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,12,20,0.45)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 캐릭터 대사 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#B388FF", // 라벤더퍼플 mega(핵심 용어)
  quoteDim: "rgba(10,8,14,0.88)",
  quoteText: "#F1EAFF",
};

// ── 📈 증권 테넌트 특화 16:9 8종(2026-07-19 Step 2) — gongdocu/safetoon 오버레이 골격 복제·전용 스킨. 스킨 색은 증권 스펙 정본.
// 📊 marketbrief(마켓브리핑 데일리 시황 브리핑) — 저투명 다크 자막바+마켓블루 badge 국면 라벨(포인트·흐름·관전)+시그널옐로 핵심 인용.
const MARKETBRIEF_SKIN: OverlaySkin = {
  captionBarBg: "rgba(4,10,18,0.6)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFD84D", // 시그널옐로 — 핵심 문장 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#2E7DF6", // 마켓블루 badge(국면 라벨)
  quoteDim: "rgba(3,8,14,0.9)",
  quoteText: "#EAF2FF",
};
// 🎙 analystnote(전문가노트 간판 해설) — 저투명 다크 자막바+딥그린 badge 국면 라벨(배경·구조·관점)+아이보리 어록 인용.
const ANALYSTNOTE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F2EAD8", // 아이보리 — 전문가 어록 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#1B8A5A", // 딥그린 badge(국면 라벨)
  quoteDim: "rgba(6,8,6,0.9)",
  quoteText: "#F5F1E6",
};
// 🧠 mindtheater(투자심리극장 행동경제 교훈 서사) — 저투명 퍼플다크 자막바+퍼플 badge 국면 라벨(유혹·함정·깨달음)+웜핑크 내면 인용.
const MINDTHEATER_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,6,14,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FF8FA3", // 웜핑크 — 내면의 말 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#8E6CEF", // 퍼플 badge(국면 라벨)
  quoteDim: "rgba(8,5,12,0.9)",
  quoteText: "#F5EAFB",
};
// 🏛 finhistory(시장의역사 금융사 아카이브 다큐) — 저투명 다크 자막바+앰버 mega 연도·수치+스틸그레이 기록 인용.
const FINHISTORY_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#B0BEC5", // 스틸그레이 — 기록 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFB300", // 앰버 mega(연도·수치)
  quoteDim: "rgba(8,8,8,0.9)",
  quoteText: "#ECEFF1",
};
// 📈 chartlab(차트연구소 개념·구조 도해) — 저투명 다크 자막바+네온그린 badge 원리 라벨(원리·구조·체크)+시안 인용. 무마스코트 도해.
const CHARTLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(4,8,16,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#4DD8E6", // 시안 — 원리 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#38E58C", // 네온그린 badge(원리 라벨)
  quoteDim: "rgba(3,6,12,0.9)",
  quoteText: "#E6FBFF",
};
// 🤖 virtualdesk(버추얼데스크 3D 아바타 앵커 브리핑) — 저투명 다크 자막바+홀로블루 mega 핵심 키워드+라이트시안 앵커 멘트 인용.
const VIRTUALDESK_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,10,20,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#7FE7F0", // 라이트시안 — 앵커 멘트 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#5AA9FF", // 홀로블루 mega(핵심 키워드)
  quoteDim: "rgba(6,8,16,0.88)",
  quoteText: "#EAF4FF",
};
// 🃏 antshow(개미극장 웹툰풍 코믹 카툰) — safetoon 구조 복제. 저투명 딥브라운 자막바+카툰옐로 mega 소동 키워드+스카이 대사 인용.
const ANTSHOW_SKIN: OverlaySkin = {
  captionBarBg: "rgba(20,16,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이블루 — 대사 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFD400", // 카툰 옐로 mega(소동 키워드)
  quoteDim: "rgba(10,8,3,0.9)",
  quoteText: "#EAF6FF",
};
// 📰 moneypress(머니신문 빈티지 신문 콜라주) — 저투명 웜다크 자막바(웜화이트 자막)+프레스레드 badge 국면 라벨(호외·맥락·전망)+크림 인용.
const MONEYPRESS_SKIN: OverlaySkin = {
  captionBarBg: "rgba(24,20,14,0.55)",
  captionText: "#F5EFE3", // 웜화이트 자막
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F0E6D2", // 크림 — 인용 따옴표 글리프(잉크블랙 대비)
  keywordVariant: "badge",
  keywordColor: "#D64545", // 프레스레드 badge(국면 라벨)
  quoteDim: "rgba(16,13,9,0.9)",
  quoteText: "#F5EFE3",
};

// ── 🏛 공공기관 테넌트 특화 16:9 8종(2026-07-20 Step 3) — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 공공 스펙 정본.
// 📋 policybrief(시정브리핑 1분 정책 브리핑) — 저투명 다크 자막바+시빅블루 badge 국면 라벨(안내·대상·방법)+화이트 핵심 인용.
const POLICYBRIEF_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,12,20,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 핵심 문장 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#3B82F6", // 시빅블루 badge(안내·대상·방법)
  quoteDim: "rgba(4,9,16,0.9)",
  quoteText: "#EAF2FF",
};
// 🎙 towntalk(타운톡 2인 토크 해설) — 저투명 웜다크 자막바+웜오렌지 badge 국면 라벨(질문·핵심·정리)+크림 인용.
const TOWNTALK_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,10,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FBE7C2", // 크림 — 정리 문장 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#F59E0B", // 웜오렌지 badge(질문·핵심·정리)
  quoteDim: "rgba(12,8,4,0.9)",
  quoteText: "#FFF3E0",
};
// 🌄 localdocu(우리고장극장 영상미 브랜딩 다큐) — 투명 자막바(웜화이트 본문)+선셋골드 구술 quote 만(keywords:false — myungjang 선례).
const LOCALDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.28)",
  captionText: "#F5ECD9", // 웜화이트 본문(스펙)
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F2B705", // 선셋골드 — 고장의 한 문장(구술) 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#F2B705",
  quoteDim: "rgba(10,8,4,0.88)",
  quoteText: "#F5ECD9",
};
// 🎬 psatheater(공익극장 공익 캠페인 재연 드라마) — 저투명 다크 자막바+캠페인레드 badge 국면 라벨(주의·확인·기억)+아이보리 인용.
const PSATHEATER_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FDF0D8", // 아이보리 — 캠페인 메시지 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#DC2626", // 캠페인레드 badge(주의·확인·기억)
  quoteDim: "rgba(10,6,6,0.9)",
  quoteText: "#FDF6E3",
};
// 🐥 townmate(동네지킴이 마스코트 캐릭터 안내) — 저투명 웜다크 자막바+병아리옐로 mega 핵심 키워드+스카이 캐릭터 인용.
const TOWNMATE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(14,12,6,0.45)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 캐릭터 대사 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFD23F", // 병아리옐로 mega(핵심 키워드)
  quoteDim: "rgba(10,8,3,0.9)",
  quoteText: "#EAF6FF",
};
// 📑 steplab(절차연구소 스텝바이스텝 도해) — 저투명 다크 자막바+민트 badge 원리 라벨(1단계·준비물·주의)+화이트 인용. 무마스코트 도해.
const STEPLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,14,24,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 정리 문장 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#10B981", // 민트 badge(1단계·준비물·주의)
  quoteDim: "rgba(4,9,16,0.9)",
  quoteText: "#EAFBF3",
};
// 🔇 signageloop(전광판극장 무음 송출면 안내 루프) — 저투명 딥네이비 자막바(본문 화이트 700 — 무음 가독 최우선)+라이트블루 badge 국면 라벨(안내·기간·문의)+화이트 인용.
const SIGNAGELOOP_SKIN: OverlaySkin = {
  captionBarBg: "rgba(4,10,18,0.6)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 핵심 안내 문장 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#40C4FF", // 라이트블루 badge(안내·기간·문의)
  quoteDim: "rgba(2,6,12,0.9)",
  quoteText: "#EAF6FF",
};
// 🖋 towncartoon(시정만평 시사만평 카툰) — 저투명 웜블랙 자막바(페이퍼크림 자막)+잉크레드 badge 국면 라벨(풍자·현실·한마디)+페이퍼크림 인용.
const TOWNCARTOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(20,18,14,0.55)",
  captionText: "#F5EFE0", // 페이퍼크림 자막
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5EFE0", // 페이퍼크림 — 정색 한 줄 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#C2410C", // 잉크레드 badge(풍자·현실·한마디)
  quoteDim: "rgba(14,12,8,0.9)",
  quoteText: "#F5EFE0",
};

// ── 🧒 유아·청소년 교육 테넌트 특화 16:9 8종(2026-07-20 Step 4) — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 교육 스펙 정본.
//   실사 4(parenttalk·classlive·growthdocu·fiveguide)+비실사 4(edumate·conceptlab·studytoon·storyart). ★교육 하드 가드 desc 는 story-styles 정본.
// 🎓 parenttalk(학부모상담소 고민 대담) — 저투명 딥퍼플다크 자막바+딥퍼플 badge 국면 라벨(고민·원인·오늘부터)+웜크림 인용.
const PARENTTALK_SKIN: OverlaySkin = {
  captionBarBg: "rgba(12,10,16,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FBE7C2", // 웜크림 — 안심 문장 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#7C5CDB", // 딥퍼플 badge(고민·원인·오늘부터)
  quoteDim: "rgba(8,6,12,0.9)",
  quoteText: "#F3ECFB",
};
// 📚 classlive(맛보기교실 강의) — 저투명 딥틸다크 자막바+틸 badge 국면 라벨(질문·핵심·기억)+레몬 인용.
const CLASSLIVE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,14,12,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFE066", // 레몬 — 딱 하나만 기억 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#14B8A6", // 틸 badge(질문·핵심·기억)
  quoteDim: "rgba(3,10,8,0.9)",
  quoteText: "#E9FBF6",
};
// 🌱 growthdocu(성장극장 성장 서사 다큐) — 투명 자막바(웜화이트 본문)+새싹그린 구술 quote 만(keywords:false — localdocu 선례).
const GROWTHDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.28)",
  captionText: "#F5ECD9", // 웜화이트 본문(스펙)
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#7BC96F", // 새싹그린 — 한 뼘의 변화(구술) 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#7BC96F",
  quoteDim: "rgba(8,10,6,0.88)",
  quoteText: "#F5ECD9",
};
// 🧭 fiveguide(오분가이드 세팅·방법 가이드) — 저투명 웜다크 자막바+오렌지 badge 국면 라벨(준비·단계·완료)+화이트 인용.
const FIVEGUIDE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(14,10,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 완료 확인 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#F97316", // 오렌지 badge(준비·단계·완료)
  quoteDim: "rgba(10,7,4,0.9)",
  quoteText: "#FFF3E6",
};
// 🐰 edumate(공부친구 유아 마스코트) — 저투명 핑크다크 자막바+캔디핑크 mega 핵심 키워드+스카이 캐릭터 인용.
const EDUMATE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,10,14,0.45)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 캐릭터 대사 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FF6FA5", // 캔디핑크 mega(핵심 후렴 키워드)
  quoteDim: "rgba(12,7,10,0.9)",
  quoteText: "#EAF6FF",
};
// 💡 conceptlab(개념연구소 교과 개념 도해) — 저투명 딥네이비 자막바+블루 badge 원리 라벨(왜·원리·예시)+화이트 인용. 무마스코트 도해.
const CONCEPTLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,12,20,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 한 줄 정리 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#3B82F6", // 블루 badge(왜·원리·예시)
  quoteDim: "rgba(4,8,14,0.9)",
  quoteText: "#EAF2FF",
};
// 📖 studytoon(학습만화극장 학습만화) — 저투명 웜다크 자막바+코믹옐로 mega 핵심 키워드+민트 인용.
const STUDYTOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,12,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#4ADEC0", // 민트 — 웃음 뒤 정리 한 컷 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFC53D", // 코믹옐로 mega(핵심 키워드)
  quoteDim: "rgba(12,8,4,0.9)",
  quoteText: "#E9FBF5",
};
// 🎨 storyart(동화극장 그림책 낭독) — 투명 자막바(웜화이트 본문)+별빛골드 구술 quote 만(keywords:false — localdocu 선례).
const STORYART_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.25)",
  captionText: "#F5ECD9", // 웜화이트 본문(스펙)
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F2C879", // 별빛골드 — 다정한 낭독 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#F2C879",
  quoteDim: "rgba(10,8,4,0.88)",
  quoteText: "#F5ECD9",
};

// ── 🛍 커머스 테넌트 특화 16:9 8종(2026-07-20 재설계 v2 "리뷰 연출이 스타일의 목적") — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 커머스 재설계 스펙 정본.
//   실사 5(luxereview·studioreview·homereview·duotalk·brandstory)+비실사 3(productlab·shoptoon·minimart). ★커머스 하드 가드 desc 는 story-styles 정본.
// ✨ luxereview(럭셔리쇼룸 럭셔리 연출 리뷰) — 저투명 블랙 자막바(웜화이트 본문)+샴페인골드 badge 국면 라벨(디테일·소재·무드)+골드 인용.
const LUXEREVIEW_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.6)",
  captionText: "#F5EFE2", // 웜화이트 본문(스펙)
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#E6C36B", // 골드 — 어울리는 순간 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#D4AF6A", // 샴페인골드 badge(디테일·소재·무드)
  quoteDim: "rgba(8,6,2,0.92)",
  quoteText: "#F5EAD0",
};
// 🎬 studioreview(스튜디오리뷰 고퀄 스튜디오 리뷰) — 저투명 다크블루 자막바+일렉트릭블루 badge 국면 라벨(컷·디테일·한 줄 평)+화이트 인용.
const STUDIOREVIEW_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,8,12,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 한 줄 평 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#4C7DFF", // 일렉트릭블루 badge(컷·디테일·한 줄 평)
  quoteDim: "rgba(4,6,10,0.9)",
  quoteText: "#EAF0FF",
};
// 🏠 homereview(홈리뷰 가정 라이프스타일 리뷰) — 저투명 웜다크 자막바+웜테라코타 badge 국면 라벨(일상·좋은 점·아쉬운 점)+크림 인용.
const HOMEREVIEW_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,12,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 어떤 집에 어울리는지 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#E07A5F", // 웜테라코타 badge(일상·좋은 점·아쉬운 점)
  quoteDim: "rgba(12,9,6,0.88)",
  quoteText: "#FFF6EA",
};
// 🗣 duotalk(듀오리뷰 티키타카 토크 리뷰) — 저투명 다크 자막바+라임 badge 국면 라벨(주장·반박·결론)+스카이 인용.
const DUOTALK_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,12,10,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 두 사람의 한 줄 평 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#A3E635", // 라임 badge(주장·반박·결론)
  quoteDim: "rgba(8,10,8,0.9)",
  quoteText: "#F0FFF4",
};
// 📜 brandstory(브랜드연대기 역사 서사) — 투명 자막바(웜화이트 본문)+브론즈 구술 quote 만(keywords:false — myungjang 선례).
const BRANDSTORY_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.28)",
  captionText: "#F5ECD9", // 웜화이트 본문(스펙)
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#CD9B62", // 브론즈 — 오늘의 약속(구술) 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#CD9B62",
  quoteDim: "rgba(10,8,4,0.88)",
  quoteText: "#F5ECD9",
};
// 🔬 productlab(상품연구소 성분·원리 도해) — 저투명 딥퍼플다크 자막바+퍼플 badge 원리 라벨(재료·원리·체크)+화이트 인용. 무마스코트 도해.
const PRODUCTLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,8,16,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘 확인해볼 것 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#8B5CF6", // 퍼플 badge(재료·원리·체크)
  quoteDim: "rgba(6,4,12,0.9)",
  quoteText: "#F1EAFB",
};
// 🛍 shoptoon(마트툰극장 장보기 카툰 시트콤) — 저투명 웜다크 자막바+카툰옐로 mega 핵심 키워드+스카이 인용.
const SHOPTOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(20,16,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 정색 꿀팁 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFD400", // 카툰옐로 mega(핵심 키워드)
  quoteDim: "rgba(12,10,4,0.9)",
  quoteText: "#EAF6FF",
};
// 🏪 minimart(미니어처마켓 미니어처 디오라마) — 저투명 웜다크 자막바+코랄 badge 국면 라벨(오늘의 코너·관찰·한 줄)+크림 인용.
const MINIMART_SKIN: OverlaySkin = {
  captionBarBg: "rgba(14,12,8,0.45)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 실물 세계의 한 줄 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FB7185", // 코랄 badge(오늘의 코너·관찰·한 줄)
  quoteDim: "rgba(10,8,5,0.9)",
  quoteText: "#FFF3E6",
};

// ── 🏪 소상공인·자영업 테넌트 특화 16:9 8종(2026-07-20 Step 6) — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 소상공인 스펙 정본.
//   실사 6(dalindocu·sajangdocu·golmokdocu·jangsaclass·munjeondocu·sigdocu)+비실사 2(jangsalab·golmoktoon). ★소상공인 하드 가드 desc 는 story-styles 정본.
// 🔥 dalindocu(달인극장 손기술 몰입 다큐) — 저투명 웜차콜 자막바+앰버 badge 국면 라벨(손끝·리듬·완성)+웜화이트 인용.
const DALINDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(12,8,4,0.55)",
  captionText: "#F5ECD9", // 웜화이트 본문(스펙)
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5ECD9", // 웜화이트 — 완성의 순간 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#F59E0B", // 앰버 badge(손끝·리듬·완성)
  quoteDim: "rgba(8,5,2,0.9)",
  quoteText: "#FBEFD9",
};
// 🌅 sajangdocu(사장의하루 명암 브이로그 다큐) — 저투명 새벽다크 자막바+새벽블루 badge 국면 라벨(새벽·한낮·마감)+크림 인용.
const SAJANGDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,10,16,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 마감의 정산 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#38BDF8", // 새벽블루 badge(새벽·한낮·마감)
  quoteDim: "rgba(3,7,12,0.9)",
  quoteText: "#EAF3FB",
};
// 🏮 golmokdocu(골목연대기 세월 서사) — 투명 자막바(웜화이트 본문)+홍등앰버 구술 quote 만(keywords:false — myungjang·brandstory 선례).
const GOLMOKDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.3)",
  captionText: "#F5ECD9", // 웜화이트 본문(스펙)
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#E8A65D", // 홍등앰버 — 오늘의 골목(구술) 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#E8A65D",
  quoteDim: "rgba(10,8,4,0.88)",
  quoteText: "#F5ECD9",
};
// 📊 jangsaclass(장사교실 데이터 강의) — 저투명 딥틸다크 자막바+틸 badge 국면 라벨(기준·숫자·체크)+화이트 인용.
const JANGSACLASS_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,14,12,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘의 체크 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#14B8A6", // 틸 badge(기준·숫자·체크)
  quoteDim: "rgba(3,9,8,0.9)",
  quoteText: "#E9FBF5",
};
// 🏃 munjeondocu(문전성시극장 현장 활력 무드) — 저투명 레드다크 자막바+레드코랄 badge 국면 라벨(오픈·러시·마감)+레몬 인용.
const MUNJEONDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,6,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFE066", // 레몬 — 마지막 손님 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#F43F5E", // 레드코랄 badge(오픈·러시·마감)
  quoteDim: "rgba(12,4,5,0.9)",
  quoteText: "#FFEAEC",
};
// 🍽 sigdocu(시그니처극장 시네마틱 B롤 감각) — 저투명 골드다크 자막바+골드 badge 국면 라벨(재료·디테일·완성)+크림 인용.
const SIGDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(14,12,4,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 완성의 한 컷 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#EAB308", // 골드 badge(재료·디테일·완성)
  quoteDim: "rgba(9,8,3,0.9)",
  quoteText: "#FFF6E0",
};
// 🧮 jangsalab(장사연구소 상권·마진 도해) — 저투명 인디고다크 자막바+인디고 badge 원리 라벨(원리·숫자·오늘)+화이트 인용. 무마스코트 도해.
const JANGSALAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,8,18,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘 확인해볼 것 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#6366F1", // 인디고 badge(원리·숫자·오늘)
  quoteDim: "rgba(5,5,12,0.9)",
  quoteText: "#EAEAFB",
};
// 🏘 golmoktoon(골목툰극장 자영업 카툰 시트콤) — 저투명 카툰다크 자막바+카툰오렌지 mega 핵심 키워드+스카이 인용.
const GOLMOKTOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(20,12,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 정색 한 줄 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FF8A00", // 카툰오렌지 mega(핵심 키워드)
  quoteDim: "rgba(12,7,3,0.9)",
  quoteText: "#EAF6FF",
};

// ── ⚖️ 전문직 테넌트 특화 16:9 8종(2026-07-20 Step 8) — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 전문직 스펙 정본.
//   실사 5(officedocu·counselroom·casedrama·caserecord·calendarbrief)+비실사 3(boardlab·officetoon·papertheater). ★전문직 하드 가드 desc 는 story-styles 정본.
// 💼 officedocu(사무소의하루 전문가 일상 브이로그) — 저투명 잉크다크 자막바+코발트 badge 국면 라벨(출근·서류·퇴근)+화이트 인용.
const OFFICEDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,10,16,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 하루의 정리 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#4F7CFF", // 코발트 badge(출근·서류·퇴근)
  quoteDim: "rgba(4,5,10,0.9)",
  quoteText: "#EAF0FB",
};
// 🗣 counselroom(상담실극장 사연 Q&A) — 저투명 딥틸다크 자막바+스카이 badge 국면 라벨(사연·쟁점·답변)+크림 인용.
const COUNSELROOM_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,12,16,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 오늘의 답변 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#0EA5E9", // 스카이 badge(사연·쟁점·답변)
  quoteDim: "rgba(3,8,11,0.9)",
  quoteText: "#EAF6FB",
};
// 🎭 casedrama(각색극장 사례 각색 재연) — 저투명 버건디다크 자막바+버건디 badge 국면 라벨(사연·전개·교훈)+웜화이트 인용.
const CASEDRAMA_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,8,10,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5ECD9", // 웜화이트 — 오늘의 교훈 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#C4506B", // 버건디 badge(사연·전개·교훈)
  quoteDim: "rgba(12,5,6,0.9)",
  quoteText: "#F5ECD9",
};
// 🗂 caserecord(사건기록실 확정판결 절제 서사) — 투명 자막바(화이트 본문 42)+스틸블루그레이 구술 quote 만(keywords:false — myungjang·golmokdocu 선례).
const CASERECORD_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.32)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#9DB2CE", // 스틸블루그레이 — 판결문의 문장(구술) 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#9DB2CE",
  quoteDim: "rgba(6,8,12,0.88)",
  quoteText: "#DCE6F2",
};
// 🗓 calendarbrief(실무달력 시즌 숫자 브리핑) — 저투명 앰버다크 자막바+앰버 badge 국면 라벨(이번 달·숫자·체크)+화이트 인용.
const CALENDARBRIEF_SKIN: OverlaySkin = {
  captionBarBg: "rgba(14,10,4,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 놓치면 생기는 일 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#D97706", // 앰버 badge(이번 달·숫자·체크)
  quoteDim: "rgba(9,7,3,0.9)",
  quoteText: "#FBF1E0",
};
// 🖍 boardlab(화이트보드연구소 쉬운 설명 도해) — 저투명 그린다크 자막바+그린 badge 국면 라벨(개념·도해·정리)+화이트 인용.
const BOARDLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,12,10,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘 기억할 한 줄 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#10B981", // 그린 badge(개념·도해·정리)
  quoteDim: "rgba(3,9,7,0.9)",
  quoteText: "#E7FBF2",
};
// 🖊 officetoon(사무소툰극장 사무소 카툰 시트콤) — 저투명 웜다크 자막바+옐로 mega 핵심 키워드+스카이 인용.
const OFFICETOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,14,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 정색 한 줄 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFC53D", // 옐로 mega(핵심 키워드)
  quoteDim: "rgba(12,9,4,0.9)",
  quoteText: "#EAF6FF",
};
// 📄 papertheater(페이퍼극장 페이퍼컷 레이어 일러) — 저투명 라벤더다크 자막바+라벤더 badge 국면 라벨(개념·절차·한 줄)+크림 인용.
const PAPERTHEATER_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,8,16,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 실무의 한 줄 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#A78BFA", // 라벤더 badge(개념·절차·한 줄)
  quoteDim: "rgba(6,5,10,0.9)",
  quoteText: "#F1EEFB",
};

// ── 🏠 부동산 테넌트 특화 16:9 8종(2026-07-21 Step 9) — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 부동산 스펙 정본.
//   실사 5(jipdeuridocu·imjangdocu·gyeyakclass·showroomdocu·makeoverdocu)+비실사 3(siselab·dongnetoon·minitown). ★부동산 하드 가드 desc 는 story-styles 정본.
// 🏡 jipdeuridocu(집들이극장 초대 프레임 룸투어) — 저투명 웜다크 자막바+웜코랄 badge 국면 라벨(현관·거실·뷰)+크림 인용.
const JIPDEURIDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,12,8,0.5)",
  captionText: "#F5EFE2",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 창밖의 뷰 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FF8A65", // 웜코랄 badge(현관·거실·뷰)
  quoteDim: "rgba(9,7,4,0.9)",
  quoteText: "#FBF3E6",
};
// 🚶 imjangdocu(임장극장 동네 탐험 도보 브이로그) — 저투명 그린다크 자막바+트레일그린 badge 국면 라벨(도착·걷기·총평)+화이트 인용.
const IMJANGDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,14,10,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘의 총평 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#34D399", // 트레일그린 badge(도착·걷기·총평)
  quoteDim: "rgba(3,8,5,0.9)",
  quoteText: "#E7FBF1",
};
// 🛡 gyeyakclass(계약서교실 소비자 보호 문해력 강의) — 저투명 블루다크 자막바+블루 badge 국면 라벨(서류·확인·체크)+화이트 인용.
const GYEYAKCLASS_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,12,20,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘의 체크 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#3B82F6", // 블루 badge(서류·확인·체크)
  quoteDim: "rgba(4,6,11,0.9)",
  quoteText: "#EAF1FB",
};
// 🛋 showroomdocu(쇼룸극장 미니멀 하이엔드 공간 감각) — 저투명 잉크다크 자막바+골드베이지 badge 국면 라벨(빛·소재·무드)+웜화이트 인용.
const SHOWROOMDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,10,10,0.55)",
  captionText: "#F5EFE2",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5EFE2", // 웜화이트 — 공간의 무드 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#C9A96A", // 골드베이지 badge(빛·소재·무드)
  quoteDim: "rgba(7,7,6,0.9)",
  quoteText: "#F5EFE2",
};
// 🔄 makeoverdocu(집단장극장 공간 전환 비포애프터) — 저투명 퍼플다크 자막바+퍼플 badge 국면 라벨(비포·과정·애프터)+레몬 인용.
const MAKEOVERDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(14,8,18,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFE066", // 레몬 — 애프터의 한 컷 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#A855F7", // 퍼플 badge(비포·과정·애프터)
  quoteDim: "rgba(9,5,12,0.9)",
  quoteText: "#F3EBFB",
};
// 📈 siselab(시세연구소 데이터 도해 브리핑) — 저투명 인디고다크 자막바+인디고 badge 국면 라벨(데이터·흐름·체크)+화이트 인용.
const SISELAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,8,18,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘의 체크 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#6D7CFF", // 인디고 badge(데이터·흐름·체크)
  quoteDim: "rgba(4,4,10,0.9)",
  quoteText: "#EAECFB",
};
// 🏘 dongnetoon(동네툰극장 이사·집구하기 카툰 시트콤) — 저투명 웜다크 자막바+옐로 mega 핵심 키워드+스카이 인용.
const DONGNETOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,14,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 마지막 정색 한 줄 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFB300", // 옐로 mega(핵심 키워드)
  quoteDim: "rgba(12,9,4,0.9)",
  quoteText: "#EAF6FF",
};
// 🏙 minitown(미니타운극장 아이소메트릭 동네 모형) — 저투명 시안다크 자막바+시안 badge 국면 라벨(입지·동선·한 줄)+크림 인용.
const MINITOWN_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,12,16,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 실제 세계의 한 줄 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#22D3EE", // 시안 badge(입지·동선·한 줄)
  quoteDim: "rgba(3,8,11,0.9)",
  quoteText: "#E7FAFE",
};

// ── 🎓 입시교육 테넌트 특화 16:9 8종(2026-07-21 Step 10) — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 입시 스펙 정본.
//   실사 5(myeonggangdocu·dokseodocu·jeonlyakbrief·suheomdocu·hapgyeokdocu)+비실사 3(plannerlab·gyosiltoon·pixelstudy). ★입시 하드 가드 desc 는 story-styles 정본.
// 🎤 myeonggangdocu(명강극장 강사 어록·명장면) — 저투명 크림슨다크 자막바+크림슨 badge 국면 라벨(몰입·정곡·어록)+웜화이트 인용.
const MYEONGGANGDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,6,8,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5ECD9", // 웜화이트 — 여운의 어록 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#EF4444", // 크림슨 badge(몰입·정곡·어록)
  quoteDim: "rgba(9,4,5,0.9)",
  quoteText: "#F7F0E0",
};
// 📚 dokseodocu(독서실극장 스터디윗미 몰입·quote-only) — 투명 자막바(화이트 본문 42)+미드나잇스카이 구술 quote 만(keywords:false — caserecord 선례).
const DOKSEODOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.32)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#A5C8FF", // 미드나잇스카이 — 소등의 하루 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#A5C8FF",
  quoteDim: "rgba(5,7,11,0.88)",
  quoteText: "#DCE6F8",
};
// 📊 jeonlyakbrief(전략브리핑 데이터 권위 리포트) — 저투명 딥그린다크 자막바+딥그린 badge 국면 라벨(흐름·데이터·기준)+화이트 인용.
const JEONLYAKBRIEF_SKIN: OverlaySkin = {
  captionBarBg: "rgba(4,14,10,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 선택의 기준 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#059669", // 딥그린 badge(흐름·데이터·기준)
  quoteDim: "rgba(3,9,6,0.9)",
  quoteText: "#EAF5EF",
};
// 🌅 suheomdocu(수험일기극장 수험생활 감정 브이로그) — 저투명 라일락다크 자막바+라일락 badge 국면 라벨(아침·교실·밤)+크림 인용.
const SUHEOMDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,8,16,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 밤의 정리 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#9F7AEA", // 라일락 badge(아침·교실·밤)
  quoteDim: "rgba(7,5,11,0.9)",
  quoteText: "#F6EEFB",
};
// 🎓 hapgyeokdocu(합격순간극장 클라이맥스 감정 서사) — 저투명 골드다크 자막바+골드 badge 국면 라벨(디데이·순간·함께)+웜화이트 인용.
const HAPGYEOKDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,12,4,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5ECD9", // 웜화이트 — 서로의 품 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#F5B301", // 골드 badge(디데이·순간·함께)
  quoteDim: "rgba(9,7,3,0.9)",
  quoteText: "#FBF3E6",
};
// ✍️ plannerlab(플래너극장 손글씨 문구 일러) — 저투명 로즈다크 자막바+로즈 badge 국면 라벨(계획·체크·오늘)+크림 인용.
const PLANNERLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,8,12,0.45)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 오늘의 체크 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FB7185", // 로즈 badge(계획·체크·오늘)
  quoteDim: "rgba(10,6,8,0.9)",
  quoteText: "#FBEEF0",
};
// 🏫 gyosiltoon(교실툰극장 교실 일상 카툰 시트콤) — 저투명 웜다크 자막바+옐로 mega 핵심 키워드+스카이 인용.
const GYOSILTOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,14,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 애정 한 줄 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFD43B", // 옐로 mega(핵심 키워드)
  quoteDim: "rgba(11,9,4,0.9)",
  quoteText: "#EAF6FF",
};
// 🕹 pixelstudy(공부픽셀극장 도트 게임 은유) — 저투명 그린다크 자막바+네온그린 badge 국면 라벨(퀘스트·경험치·레벨업)+레몬 인용.
const PIXELSTUDY_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,14,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFE066", // 레몬 — 레벨업 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#4ADE80", // 네온그린 badge(퀘스트·경험치·레벨업)
  quoteDim: "rgba(4,10,6,0.9)",
  quoteText: "#FDF6D8",
};

// ── 🌏 외국인교육 테넌트 특화 16:9 8종(2026-07-21 Step 11) — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 외국인교육 스펙 정본.
//   실사 5(hangeulclass·campusdocu·culturebridge·interviewdocu·welcomedocu)+비실사 3(settlelab·hangeultoon·scrapbooklab). ★하드 가드 desc 는 story-styles 정본.
// 📖 hangeulclass(한글교실극장 따뜻한 한국어 강의) — 저투명 소프트레드다크 자막바+소프트레드 badge 국면 라벨(표현·예문·따라하기)+크림 인용.
const HANGEULCLASS_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,8,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 따라 말하기 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#F87171", // 소프트레드 badge(표현·예문·따라하기)
  quoteDim: "rgba(10,5,5,0.9)",
  quoteText: "#FBEFE4",
};
// 🎒 campusdocu(캠퍼스극장 유학 생활 동경 브이로그) — 저투명 캠퍼스블루다크 자막바+캠퍼스블루 badge 국면 라벨(도착·캠퍼스·저녁)+화이트 인용.
const CAMPUSDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,12,20,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 저녁의 여운 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#60A5FA", // 캠퍼스블루 badge(도착·캠퍼스·저녁)
  quoteDim: "rgba(5,7,12,0.9)",
  quoteText: "#EAF1FB",
};
// 🎧 culturebridge(컬처브릿지극장 K-culture 연계 학습) — 저투명 마젠타다크 자막바+마젠타 badge 국면 라벨(장면·표현·내 것)+레몬 인용.
const CULTUREBRIDGE_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,6,16,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFE066", // 레몬 — 내 것으로 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#E879F9", // 마젠타 badge(장면·표현·내 것)
  quoteDim: "rgba(9,4,9,0.9)",
  quoteText: "#F9E6FA",
};
// 🎙 interviewdocu(거리인터뷰극장 다관점 존중 인터뷰) — 저투명 선셋오렌지다크 자막바+선셋오렌지 badge 국면 라벨(질문·관점·한 마디)+화이트 인용.
const INTERVIEWDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,10,4,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 여운의 한 마디 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FF9F43", // 선셋오렌지 badge(질문·관점·한 마디)
  quoteDim: "rgba(9,6,3,0.9)",
  quoteText: "#FBF1E6",
};
// 🤝 welcomedocu(환대극장 정착의 순간 감성 다큐) — 저투명 티일다크 자막바+티일 badge 국면 라벨(첫날·손길·미소)+웜화이트 인용.
const WELCOMEDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(4,14,12,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5ECD9", // 웜화이트 — 서로의 미소 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#2DD4BF", // 티일 badge(첫날·손길·미소)
  quoteDim: "rgba(3,9,8,0.9)",
  quoteText: "#EAF6F3",
};
// 📑 settlelab(정착연구소 절차 인포그래픽) — 저투명 인디고다크 자막바+인디고 badge 국면 라벨(서류·절차·체크)+화이트 인용.
const SETTLELAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,8,18,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 체크 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#818CF8", // 인디고 badge(서류·절차·체크)
  quoteDim: "rgba(5,5,11,0.9)",
  quoteText: "#EBECFB",
};
// 🖌 hangeultoon(한글툰극장 적응기 카툰 시트콤) — 저투명 웜다크 자막바+옐로 mega 핵심 키워드+스카이 인용.
const HANGEULTOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,14,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 애정 한 줄 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFD93D", // 옐로 mega(핵심 키워드)
  quoteDim: "rgba(11,9,4,0.9)",
  quoteText: "#EAF6FF",
};
// 📔 scrapbooklab(스크랩북극장 한국 발견 콜라주) — 저투명 핑크다크 자막바+핑크 badge 국면 라벨(발견·조각·기록)+크림 인용.
const SCRAPBOOKLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,10,14,0.45)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 오늘의 한 조각 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#F472B6", // 핑크 badge(발견·조각·기록)
  quoteDim: "rgba(10,6,8,0.9)",
  quoteText: "#FBEAF2",
};

// ── ⚕️ 병의원 테넌트 특화 16:9 8종(2026-07-21 Step 12) — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 병의원 스펙 정본.
//   실사 4(jinryodocu·yebangclass·byeongwondocu·annaedocu)+비실사 4(bodylab·clayclinic·byeongwontoon·ansimart). ★하드 가드 desc 는 story-styles 정본.
// 🩺 jinryodocu(진료실극장 원장 지식 해설) — 저투명 메디다크 자막바+메디블루 badge 국면 라벨(증상·원리·관리)+화이트 인용.
const JINRYODOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,12,18,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘의 관리 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#4CA7F0", // 메디블루 badge(증상·원리·관리)
  quoteDim: "rgba(4,8,12,0.9)",
  quoteText: "#EAF2FA",
};
// 🌿 yebangclass(건강습관교실 생활 예방 강의) — 저투명 그린다크 자막바+그린 badge 국면 라벨(습관·오늘·체크)+크림 인용.
const YEBANGCLASS_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,14,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 체크 한 줄 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#66BB6A", // 그린 badge(습관·오늘·체크)
  quoteDim: "rgba(4,9,5,0.9)",
  quoteText: "#EFF6E9",
};
// 🏥 byeongwondocu(병원의하루 의료진 일상 브이로그) — 저투명 바이올렛다크 자막바+소프트바이올렛 badge 국면 라벨(아침·회진·마감)+웜화이트 인용.
const BYEONGWONDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,8,16,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5ECD9", // 웜화이트 — 하루의 마감 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#B39DDB", // 소프트바이올렛 badge(아침·회진·마감)
  quoteDim: "rgba(6,5,10,0.9)",
  quoteText: "#EEEAF5",
};
// 🧭 annaedocu(첫진료안내극장 절차 안내 안심) — 저투명 스카이다크 자막바+스카이 badge 국면 라벨(접수·진행·안내)+화이트 인용.
const ANNAEDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,12,16,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 끝나고 나면 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#4FC3F7", // 스카이 badge(접수·진행·안내)
  quoteDim: "rgba(4,8,11,0.9)",
  quoteText: "#EAF4FB",
};
// 🫀 bodylab(인체연구소 의학 해부 도해) — 저투명 코랄다크 자막바+코랄레드 badge 국면 라벨(구조·원리·관리)+화이트 인용.
const BODYLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,8,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘 확인 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FF6B6B", // 코랄레드 badge(구조·원리·관리)
  quoteDim: "rgba(11,5,5,0.9)",
  quoteText: "#FBEAEA",
};
// 🧸 clayclinic(클레이클리닉극장 클레이 미니 병원) — 저투명 웜다크 자막바+피치 badge 국면 라벨(진찰·처방·한 줄)+크림 인용.
const CLAYCLINIC_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,12,8,0.45)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 안심의 한 마디 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FFAB91", // 피치 badge(진찰·처방·한 줄)
  quoteDim: "rgba(10,7,4,0.9)",
  quoteText: "#FBF0E6",
};
// 😷 byeongwontoon(병원툰극장 병원 카툰 시트콤) — 저투명 웜다크 자막바+옐로 mega 핵심 키워드+스카이 인용.
const BYEONGWONTOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,14,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 정색 건강 한 줄 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFCA28", // 옐로 mega(핵심 키워드)
  quoteDim: "rgba(11,9,4,0.9)",
  quoteText: "#EAF6FF",
};
// 🎨 ansimart(안심일러극장 수채 안심 감성·quote-only) — 투명 자막바+세이지그린 quote 만(keywords:false·dokseodocu·caserecord 선례).
const ANSIMART_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.3)",
  captionText: "#F5ECD9",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#C5E1A5", // 세이지그린 — 절제된 위로 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#C5E1A5",
  quoteDim: "rgba(6,10,6,0.88)",
  quoteText: "#E8F1DC",
};

// ── 🚗 자동차 테넌트 특화 16:9 8종(2026-07-21 Step 13) — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 자동차 스펙 정본.
//   실사 6(siseungdocu·jeongbidocu·detaildocu·maemuldocu·trackdocu·garagedocu)+비실사 2(carlab·bungbungtoon). ★하드 가드 desc 는 story-styles 정본.
// 🚗 siseungdocu(시승검증극장 냉정 검증 시승) — 저투명 건메탈다크 자막바+건메탈블루 badge 국면 라벨(첫인상·검증·결론)+화이트 인용.
const SISEUNGDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,10,18,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 담담한 결론 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#5C7CFA", // 건메탈블루 badge(첫인상·검증·결론)
  quoteDim: "rgba(5,6,11,0.9)",
  quoteText: "#EAEEFB",
};
// 🔧 jeongbidocu(정비공방극장 수리 과정 공개) — 저투명 오렌지다크 자막바+오렌지 badge 국면 라벨(진단·과정·완료)+웜화이트 인용.
const JEONGBIDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,10,4,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5ECD9", // 웜화이트 — 말끔한 완료 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FF922B", // 오렌지 badge(진단·과정·완료)
  quoteDim: "rgba(10,7,3,0.9)",
  quoteText: "#F7EEDD",
};
// ✨ detaildocu(광택극장 세차·디테일링 감각) — 저투명 아쿠아다크 자막바+아쿠아 badge 국면 라벨(비포·디테일·광택)+크림 인용.
const DETAILDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(4,12,16,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 광택의 한 컷 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#3BC9DB", // 아쿠아 badge(비포·디테일·광택)
  quoteDim: "rgba(3,8,10,0.9)",
  quoteText: "#E6F7FA",
};
// 🚙 maemuldocu(매물투명극장 실물 있는 그대로) — 저투명 그린다크 자막바+그린 badge 국면 라벨(실물·이력·확인)+화이트 인용.
const MAEMULDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,14,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 이력의 정리 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#51CF66", // 그린 badge(실물·이력·확인)
  quoteDim: "rgba(4,9,5,0.9)",
  quoteText: "#EAF6EC",
};
// 🏁 trackdocu(서킷극장 주행 감각 카타르시스·트랙 한정) — 저투명 레이싱다크 자막바+레이싱레드 badge 국면 라벨(출발·코너·기록)+레몬 인용.
const TRACKDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,6,6,0.55)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFE066", // 레몬 — 기록의 한 줄 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FA5252", // 레이싱레드 badge(출발·코너·기록)
  quoteDim: "rgba(11,4,4,0.9)",
  quoteText: "#FBEAEA",
};
// 🛞 garagedocu(차고지극장 차와 사람의 정 감성·quote-only) — 투명 자막바+웜앰버 quote 만(keywords:false·ansimart·caserecord 선례).
const GARAGEDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.32)",
  captionText: "#F5ECD9",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#E0A458", // 웜앰버 — 절제된 여운 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#E0A458",
  quoteDim: "rgba(10,8,4,0.88)",
  quoteText: "#F3E7D3",
};
// 🧰 carlab(차알못연구소 부품·원리·유지비 도해) — 저투명 인디고다크 자막바+인디고 badge 국면 라벨(원리·비용·체크)+화이트 인용.
const CARLAB_SKIN: OverlaySkin = {
  captionBarBg: "rgba(8,8,18,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘 확인 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#748FFC", // 인디고 badge(원리·비용·체크)
  quoteDim: "rgba(5,5,11,0.9)",
  quoteText: "#EAECFB",
};
// 🚘 bungbungtoon(붕붕툰극장 자동차 카툰 시트콤) — 저투명 웜다크 자막바+옐로 mega 핵심 키워드+스카이 인용.
const BUNGBUNGTOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,14,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 정색 안전 한 줄 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFD836", // 옐로 mega(핵심 키워드)
  quoteDim: "rgba(11,9,4,0.9)",
  quoteText: "#EAF6FF",
};

// ── ⛪ 종교(교회) 테넌트 특화 16:9 8종(2026-07-21 Step 14) — gongdocu 오버레이 골격 복제·전용 스킨. 스킨 색은 종교 스펙 정본.
//   실사 4(malsseumdocu·gongdongchedocu·wirotalk·naturedocu)+비실사 4(mukssangart·callidocu·gyohoetoon·stainedart). ★하드 가드 desc 는 story-styles 정본.
// 🎤 malsseumdocu(말씀클립극장 설교 훅 클립) — 저투명 딥퍼플다크 자막바+딥퍼플 badge 국면 라벨(말씀·한 문장·여운)+웜화이트 인용.
const MALSSEUMDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,8,16,0.55)",
  captionText: "#F5ECD9",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5ECD9", // 웜화이트 — 여운의 한 문장 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#9775FA", // 딥퍼플 badge(말씀·한 문장·여운)
  quoteDim: "rgba(6,5,10,0.9)",
  quoteText: "#F3ECDD",
};
// 🤝 gongdongchedocu(공동체극장 교회 일상 온기) — 저투명 웜오렌지다크 자막바+웜오렌지 badge 국면 라벨(준비·함께·온기)+크림 인용.
const GONGDONGCHEDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(16,12,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 남는 온기 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#FFA94D", // 웜오렌지 badge(준비·함께·온기)
  quoteDim: "rgba(10,7,3,0.9)",
  quoteText: "#FBEEDC",
};
// 💬 wirotalk(위로상담소 삶의 언어 고민 상담) — 저투명 틸다크 자막바+틸 badge 국면 라벨(고민·공감·한 마디)+화이트 인용.
const WIROTALK_SKIN: OverlaySkin = {
  captionBarBg: "rgba(4,14,12,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFFFFF", // 화이트 — 오늘의 한 마디 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#38D9A9", // 틸 badge(고민·공감·한 마디)
  quoteDim: "rgba(3,9,8,0.9)",
  quoteText: "#E6F7F2",
};
// 🌿 naturedocu(자연말씀극장 자연 서정 위로·무인물 quote-only) — 저투명 세이지다크 자막바+웜화이트 quote 만(keywords:false·garagedocu 선례).
const NATUREDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(6,12,10,0.45)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#F5ECD9", // 웜화이트 — 마음에 남는 한 줄 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#69DB7C",
  quoteDim: "rgba(4,8,6,0.88)",
  quoteText: "#EAF4EC",
};
// 🕯 mukssangart(새벽묵상극장 수채 묵상·quote-only) — 투명 자막바+소프트골드 quote 만(keywords:false·ansimart·garagedocu 선례).
const MUKSSANGART_SKIN: OverlaySkin = {
  captionBarBg: "rgba(0,0,0,0.3)",
  captionText: "#F5ECD9",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#E9C46A", // 소프트골드 — 남는 한 문장 인용 따옴표 글리프
  keywordVariant: "float", // keywords:false 로 키워드 미파생(값은 타입 충족용)
  keywordColor: "#E9C46A",
  quoteDim: "rgba(8,7,4,0.85)",
  quoteText: "#F3E7CE",
};
// ✍️ callidocu(말씀캘리극장 필사 감성) — 저투명 인디고블루다크 자막바+인디고블루 badge 국면 라벨(호흡·필사·여백)+크림 인용.
const CALLIDOCU_SKIN: OverlaySkin = {
  captionBarBg: "rgba(12,10,8,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFF3E0", // 크림 — 완성의 여백 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#8093F1", // 인디고블루 badge(호흡·필사·여백)
  quoteDim: "rgba(7,6,4,0.9)",
  quoteText: "#FBEEDC",
};
// ⛪ gyohoetoon(교회툰극장 카툰 시트콤) — 저투명 웜다크 자막바+옐로 mega 핵심 키워드+스카이 인용.
const GYOHOETOON_SKIN: OverlaySkin = {
  captionBarBg: "rgba(18,14,6,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 800,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#6EC6FF", // 스카이 — 잔잔한 한 줄 인용 따옴표 글리프
  keywordVariant: "mega",
  keywordColor: "#FFD65C", // 옐로 mega(핵심 키워드)
  quoteDim: "rgba(11,9,4,0.9)",
  quoteText: "#EAF6FF",
};
// 🪟 stainedart(빛유리극장 스테인드글라스 빛의 은유) — 저투명 바이올렛다크 자막바+바이올렛 badge 국면 라벨(빛·조각·완성)+레몬 인용.
const STAINEDART_SKIN: OverlaySkin = {
  captionBarBg: "rgba(10,8,18,0.5)",
  captionText: "#FFFFFF",
  captionWeight: 700,
  captionMaxRows: 2,
  captionCenterY: 0.9,
  captionRadius: 12,
  captionPadX: 48,
  captionPadY: 16,
  accent: "#FFE066", // 레몬 — 완성되는 그림 인용 따옴표 글리프
  keywordVariant: "badge",
  keywordColor: "#7048E8", // 바이올렛 badge(빛·조각·완성)
  quoteDim: "rgba(6,5,11,0.9)",
  quoteText: "#ECE6FB",
};

export const OVERLAY_SKINS: Record<OverlaySkinId, OverlaySkin> = {
  knowdoc: KNOWDOC_SKIN,
  econstory: ECONSTORY_SKIN,
  seniorlife: SENIORLIFE_SKIN,
  infoknow: INFOKNOW_SKIN,
  cosmos: COSMOS_SKIN,
  nightbook: NIGHTBOOK_SKIN,
  darkcase: DARKCASE_SKIN,
  famefall: FAMEFALL_SKIN,
  sleepdocu: SLEEPDOCU_SKIN,
  horrorrank: HORRORRANK_SKIN,
  stickfall: STICKFALL_SKIN,
  jinsei: JINSEI_SKIN,
  gyakuten: GYAKUTEN_SKIN,
  sengoku: SENGOKU_SKIN,
  kaidan: KAIDAN_SKIN,
  teikoku: TEIKOKU_SKIN,
  nemureru: NEMURERU_SKIN,
  yadam: YADAM_SKIN,
  verdict: VERDICT_SKIN,
  geomap: GEOMAP_SKIN,
  wisdom: WISDOM_SKIN,
  gunggeum: GUNGGEUM_SKIN,
  trivia: TRIVIA_SKIN,
  briefing: BRIEFING_SKIN,
  principleExplainer: PRINCIPLE_EXPLAINER_SKIN,
  blueprintGov: BLUEPRINT_GOV_SKIN,
  blueprintStudio: BLUEPRINT_STUDIO_SKIN,
  blueprintSite: BLUEPRINT_SITE_SKIN,
  blueprintCorp: BLUEPRINT_CORP_SKIN,
  blueprintLecture: BLUEPRINT_LECTURE_SKIN,
  blueprintMotion: BLUEPRINT_MOTION_SKIN,
  blueprintShowreel: BLUEPRINT_SHOWREEL_SKIN,
  blueprintColorCut: BLUEPRINT_COLORCUT_SKIN,
  gongdocu: GONGDOCU_SKIN,
  safetoon: SAFETOON_SKIN,
  zoosite: ZOOSITE_SKIN,
  courtcase: COURTCASE_SKIN,
  structlab: STRUCTLAB_SKIN,
  myungjang: MYUNGJANG_SKIN,
  sagorecord: SAGORECORD_SKIN,
  safenomics: SAFENOMICS_SKIN,
  sagobrief: SAGOBRIEF_SKIN,
  insucase: INSUCASE_SKIN,
  yakgwanlab: YAKGWANLAB_SKIN,
  lifeplan: LIFEPLAN_SKIN,
  fraudfile: FRAUDFILE_SKIN,
  insushow: INSUSHOW_SKIN,
  fpchronicle: FPCHRONICLE_SKIN,
  moneyclass: MONEYCLASS_SKIN,
  insumate: INSUMATE_SKIN,
  marketbrief: MARKETBRIEF_SKIN,
  analystnote: ANALYSTNOTE_SKIN,
  mindtheater: MINDTHEATER_SKIN,
  finhistory: FINHISTORY_SKIN,
  chartlab: CHARTLAB_SKIN,
  virtualdesk: VIRTUALDESK_SKIN,
  antshow: ANTSHOW_SKIN,
  moneypress: MONEYPRESS_SKIN,
  policybrief: POLICYBRIEF_SKIN,
  towntalk: TOWNTALK_SKIN,
  localdocu: LOCALDOCU_SKIN,
  psatheater: PSATHEATER_SKIN,
  townmate: TOWNMATE_SKIN,
  steplab: STEPLAB_SKIN,
  signageloop: SIGNAGELOOP_SKIN,
  towncartoon: TOWNCARTOON_SKIN,
  parenttalk: PARENTTALK_SKIN,
  classlive: CLASSLIVE_SKIN,
  growthdocu: GROWTHDOCU_SKIN,
  fiveguide: FIVEGUIDE_SKIN,
  edumate: EDUMATE_SKIN,
  conceptlab: CONCEPTLAB_SKIN,
  studytoon: STUDYTOON_SKIN,
  storyart: STORYART_SKIN,
  luxereview: LUXEREVIEW_SKIN,
  studioreview: STUDIOREVIEW_SKIN,
  homereview: HOMEREVIEW_SKIN,
  duotalk: DUOTALK_SKIN,
  brandstory: BRANDSTORY_SKIN,
  productlab: PRODUCTLAB_SKIN,
  shoptoon: SHOPTOON_SKIN,
  minimart: MINIMART_SKIN,
  // 🏪 소상공인·자영업 16:9 8종(2026-07-20 Step 6)
  dalindocu: DALINDOCU_SKIN,
  sajangdocu: SAJANGDOCU_SKIN,
  golmokdocu: GOLMOKDOCU_SKIN,
  jangsaclass: JANGSACLASS_SKIN,
  munjeondocu: MUNJEONDOCU_SKIN,
  sigdocu: SIGDOCU_SKIN,
  jangsalab: JANGSALAB_SKIN,
  golmoktoon: GOLMOKTOON_SKIN,
  // ⚖️ 전문직 16:9 8종(2026-07-20 Step 8)
  officedocu: OFFICEDOCU_SKIN,
  counselroom: COUNSELROOM_SKIN,
  casedrama: CASEDRAMA_SKIN,
  caserecord: CASERECORD_SKIN,
  calendarbrief: CALENDARBRIEF_SKIN,
  boardlab: BOARDLAB_SKIN,
  officetoon: OFFICETOON_SKIN,
  papertheater: PAPERTHEATER_SKIN,
  // 🏠 부동산 16:9 8종(2026-07-21 Step 9)
  jipdeuridocu: JIPDEURIDOCU_SKIN,
  imjangdocu: IMJANGDOCU_SKIN,
  gyeyakclass: GYEYAKCLASS_SKIN,
  showroomdocu: SHOWROOMDOCU_SKIN,
  makeoverdocu: MAKEOVERDOCU_SKIN,
  siselab: SISELAB_SKIN,
  dongnetoon: DONGNETOON_SKIN,
  minitown: MINITOWN_SKIN,
  // 🎓 입시교육 16:9 8종(2026-07-21 Step 10)
  myeonggangdocu: MYEONGGANGDOCU_SKIN,
  dokseodocu: DOKSEODOCU_SKIN,
  jeonlyakbrief: JEONLYAKBRIEF_SKIN,
  suheomdocu: SUHEOMDOCU_SKIN,
  hapgyeokdocu: HAPGYEOKDOCU_SKIN,
  plannerlab: PLANNERLAB_SKIN,
  gyosiltoon: GYOSILTOON_SKIN,
  pixelstudy: PIXELSTUDY_SKIN,
  // 🌏 외국인교육 16:9 8종(2026-07-21 Step 11)
  hangeulclass: HANGEULCLASS_SKIN,
  campusdocu: CAMPUSDOCU_SKIN,
  culturebridge: CULTUREBRIDGE_SKIN,
  interviewdocu: INTERVIEWDOCU_SKIN,
  welcomedocu: WELCOMEDOCU_SKIN,
  settlelab: SETTLELAB_SKIN,
  hangeultoon: HANGEULTOON_SKIN,
  scrapbooklab: SCRAPBOOKLAB_SKIN,
  jinryodocu: JINRYODOCU_SKIN,
  yebangclass: YEBANGCLASS_SKIN,
  byeongwondocu: BYEONGWONDOCU_SKIN,
  annaedocu: ANNAEDOCU_SKIN,
  bodylab: BODYLAB_SKIN,
  clayclinic: CLAYCLINIC_SKIN,
  byeongwontoon: BYEONGWONTOON_SKIN,
  ansimart: ANSIMART_SKIN,
  // 🚗 자동차 16:9 8종(2026-07-21 Step 13)
  siseungdocu: SISEUNGDOCU_SKIN,
  jeongbidocu: JEONGBIDOCU_SKIN,
  detaildocu: DETAILDOCU_SKIN,
  maemuldocu: MAEMULDOCU_SKIN,
  trackdocu: TRACKDOCU_SKIN,
  garagedocu: GARAGEDOCU_SKIN,
  carlab: CARLAB_SKIN,
  bungbungtoon: BUNGBUNGTOON_SKIN,
  // ⛪ 종교(교회) 16:9 8종
  malsseumdocu: MALSSEUMDOCU_SKIN,
  gongdongchedocu: GONGDONGCHEDOCU_SKIN,
  wirotalk: WIROTALK_SKIN,
  naturedocu: NATUREDOCU_SKIN,
  mukssangart: MUKSSANGART_SKIN,
  callidocu: CALLIDOCU_SKIN,
  gyohoetoon: GYOHOETOON_SKIN,
  stainedart: STAINEDART_SKIN,
};


/** 자막 접기 폭(em) — 스킨 비율(미지정=CAPTION_WIDTH_RATIO_DEFAULT) × 화면폭 ÷ 폰트 픽셀.
 *  렌더와 테스트가 ★같은 식★을 보도록 순수 함수로 노출한다(테스트가 식을 손으로 베끼면 조용히 낡는다). */
export function captionMaxEmFor(skin: Pick<OverlaySkin, "captionWidthRatio">, width: number, capSizePx: number): number {
  return (width * (skin.captionWidthRatio ?? CAPTION_WIDTH_RATIO_DEFAULT)) / Math.max(1, capSizePx);
}

/** 한 줄을 maxEm 폭으로 어절 접기 후 최대 maxRows 물리줄로 캡(초과분은 마지막 줄에 합침). */
export function foldRows(text: string, maxEm: number, maxRows: number): string[] {
  const wrapped = wrapDisplayLineEm(text ?? "", maxEm).split("\n");
  if (wrapped.length <= maxRows) return wrapped;
  return [...wrapped.slice(0, maxRows - 1), wrapped.slice(maxRows - 1).join(" ")];
}

